import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  MenuItem,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Avatar,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Divider,
  Snackbar,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Send,
  Delete,
  Edit,
  CheckCircle,
  MailOutline,
  Reply,
  FileUpload,
  CloudDone,
  MarkEmailRead,
  MarkEmailUnread,
  Add,
  Save,
  Inbox,
  WarningAmber,
  Campaign,
  Settings,
  Mail,
  Check,
  PeopleAlt,
  Layers,
  Refresh,
  Close,
  Visibility,
  VolumeUp,
  Groups,
  ExpandMore,
  ExpandLess,
  AccountTree,
  Analytics,
  Schedule,
  TrendingUp,
  ThumbUp,
  MessageSquare,
  MoreVert,
  Search,
  Download,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatClear,
  Link as LinkIcon,
  Title,
  FormatQuote,
  Code,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import AdvancedEmailComposer from './AdvancedEmailComposer';
import EmailStudioHeader from './EmailStudioHeader';
import AccountsManager from './AccountsManager';
import CampaignPlanner from './CampaignPlanner';
import InboxManager from './InboxManager';
import TrackingAnalytics from './TrackingAnalytics';
import TemplateManager from './TemplateManager';
import { useEmailManager, normalizeEmails } from '../../hooks/useEmailManager';
import { readFileAsBase64 } from '../../utils/file';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../services/adminApi';
import { templateApi, TemplateApiError } from '../../services/templateApi';
import {
  EMAIL_PROVIDERS,
  detectProviderByEmail,
  applyProviderTemplate,
  getProviderHelp,
  getProviderOptions,
  validateAccountForm,
} from '../../utils/emailProviders';

const defaultAccount = {
  label: '',
  provider: 'SMTP',
  fromName: '',
  fromEmail: '',
  host: '',
  port: 587,
  secure: true,
  username: '',
  password: '',
  imapHost: '',
  imapPort: 993,
  imapSecure: true,
  mailbox: 'INBOX',
  popHost: '',
  popPort: 995,
  popSecure: true,
  dailyLimit: 0,
};

const mergeRecipients = (current = [], next = []) =>
  normalizeEmails([...(current || []), ...(next || [])]);

const isValidEmail = (value = '') => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

const extractCarrierEmails = (carrierData) => {
  if (!carrierData) return [];
  const pool = [];
  const consider = (row) => {
    if (!row) return;
    const candidates = [
      row.email,
      row.email_address,
      row.contactEmail,
      row.dispatchEmail,
    ].filter(Boolean);
    candidates.forEach((v) => pool.push(v));
  };
  if (Array.isArray(carrierData)) {
    carrierData.forEach(consider);
  } else if (carrierData.searchType === 'batch') {
    (carrierData.batchResults || []).forEach(consider);
  } else {
    consider(carrierData);
  }
  return normalizeEmails(pool);
};
const EmailWorkspace = ({ carrierData }) => {
  const { isAuthenticated, token } = useAuth();
  const envApiBase = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL || '';
  const apiBase = useMemo(() => {
    if (typeof window === 'undefined') return envApiBase || '';
    return window.location.origin || '';
  }, [envApiBase]);
  const apiUrl = useCallback((path) => `${apiBase}${path}`, [apiBase]);
  const authHeaders = useMemo(
    () =>
      token
        ? { Authorization: `Bearer ${token}`, 'X-Auth-Token': token }
        : {},
    [token]
  );
  const {
    accounts,
    selectedAccountId,
    setSelectedAccountId,
    inbox,
    sent,
    sending,
    statusText,
    sendProgress,
    addAccount,
    updateAccount,
    deleteAccount,
    testAccount,
    sendEmail,
    fetchTrackingStats,
    fetchTrackingTotals,
    setMessageStatus,
    replyToMessage,
    deleteMessage,
    fetchInbox,
    fetchingInbox,
    stats,
    prefsError,
  } = useEmailManager();

  const [accountForm, setAccountForm] = useState(defaultAccount);
  const [editingId, setEditingId] = useState('');
  const [accountMessage, setAccountMessage] = useState('');
  const [accountError, setAccountError] = useState('');
  const [testPassed, setTestPassed] = useState(false);
  const [testingAccount, setTestingAccount] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [selectedProviderTemplate, setSelectedProviderTemplate] = useState(null);
  const [showProviderGuide, setShowProviderGuide] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountProviderFilter, setAccountProviderFilter] = useState('');
  const [compactAccounts, setCompactAccounts] = useState(true);
  const [bulkTesting, setBulkTesting] = useState(false);
  const [bulkTestSummary, setBulkTestSummary] = useState({ success: 0, failed: 0 });
  const [accountGroups, setAccountGroups] = useState([]);
  const [groupOpen, setGroupOpen] = useState({});
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({ id: '', name: '', accountIds: [] });

  const [compose, setCompose] = useState({
    accountId: '',
    subject: '',
    body: '',
    recipients: [],
    manualBlock: '',
  });
  const [composeRecipientRows, setComposeRecipientRows] = useState({ headers: [], rows: [], byEmail: {} });
  const [sendError, setSendError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const isComposeTab = activeTab === 1;
  const [activeMessageId, setActiveMessageId] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});
  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const [replyAttachments, setReplyAttachments] = useState([]); // kept for compatibility; attachments not sent in reply
  const [replySending, setReplySending] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [notice, setNotice] = useState({ open: false, type: 'info', message: '', scope: 'global' });
  const [trackingMap, setTrackingMap] = useState({});
  const [trackingTotals, setTrackingTotals] = useState({ totalOpens: 0 });
  const [seenLoading, setSeenLoading] = useState(false);
  const [seenPage, setSeenPage] = useState(1);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [threads, setThreads] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [trackingData, setTrackingData] = useState([]);
  const [emailStats, setEmailStats] = useState({});
  const [campaignAnalytics, setCampaignAnalytics] = useState({});
  const notificationsInitializedRef = useRef(false);
  const defaultCampaignPlan = {
    selectedTemplates: [],
    selectedAccounts: [],
    perAccountDaily: 50,
    perAccountLimits: {},
    batchSize: 10,
    minDelaySec: 30,
    maxDelaySec: 90,
    shuffleTemplates: true,
    rotateAccounts: true,
    startOffsetSec: 0,
    jitterSec: 5,
    batchPauseEvery: 10,
    batchPauseSec: 120,
    maxPerAccountPerHour: 40,
    maxFailures: 5,
    enforceMinDelay: true,
    trackOpens: true,
    trackClicks: true,
  };

  const [campaignTemplates, setCampaignTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState({ id: '', name: '', subject: '', body: '' });
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const templateBodyRef = useRef(null);
  const [campaignPlan, setCampaignPlan] = useState(defaultCampaignPlan);
  const [campaignRun, setCampaignRun] = useState({ id: null, counts: {}, running: false });
  const [plannerStats, setPlannerStats] = useState({});
  const [campaignTrackOpens, setCampaignTrackOpens] = useState(true);
  const [savedPlans, setSavedPlans] = useState([]);
  const [composePlanId, setComposePlanId] = useState('');
  const [composeTemplateId, setComposeTemplateId] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [inboxSearch, setInboxSearch] = useState('');
  const [engagedSearch, setEngagedSearch] = useState('');
  const [engagedMinOpens, setEngagedMinOpens] = useState(0);
  const campaignPollRef = useRef(null);
  const [plannerLoaded, setPlannerLoaded] = useState(false);
  const initialSyncSkipped = useRef(false);
  const uiPrefsSyncSkipped = useRef(false);
  const uiPrefsTimer = useRef(null);
  const inboxAutoRefreshRef = useRef(null);
  const gravatarCache = useRef({});
  const [planOpen, setPlanOpen] = useState({});
  const [plannerRecipientColumns, setPlannerRecipientColumns] = useState([]);
  const mergeTagOptions = useMemo(() => {
    const explicit = [
      'Legal Name',
      'DBA',
      'DOT',
      'MC',
      'State(Physical)',
      'State(Mailing)',
      'Phone',
      'Email',
      'Status',
      'Carrier Operation',
      'Power Units',
      'Drivers',
      'Active Duration',
      'Physical Address',
      'Mailing Address',
    ];
    const fromCompose = composeRecipientRows.headers || [];
    const fromPlanner = plannerRecipientColumns || [];
    const combined = [...explicit, ...fromCompose, ...fromPlanner]
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    return Array.from(new Set(combined)).map((key) => `{{${key}}}`);
  }, [composeRecipientRows.headers, plannerRecipientColumns]);
  const filteredTemplates = useMemo(() => {
    const query = String(templateSearch || '').trim().toLowerCase();
    if (!query) return campaignTemplates;
    return campaignTemplates.filter((template) => {
      const hay = `${template.name || ''} ${template.subject || ''}`.toLowerCase();
      return hay.includes(query);
    });
  }, [campaignTemplates, templateSearch]);
  const updateSavedPlan = useCallback((id, updater) => {
    setSavedPlans((prev) => prev.map((p) => (p.id === id ? updater(p) : p)));
  }, []);
  const [notificationPrefs, setNotificationPrefs] = useState({
    newEmail: true,
    seen: true,
    opens: true,
    sound: true,
  });
  const prevUnreadCount = useRef(0);
  const prevReadIds = useRef(new Set());
  const [footerNotice, setFooterNotice] = useState({ open: false, message: '' });
  const prevTotalOpens = useRef(0);
  const engagedGroups = useMemo(() => {
    const groups = {};
    sent.forEach((s) => {
      const mid = s.messageId || s.id;
      const stats = trackingMap[mid];
      if (!stats || (stats.totalOpens || 0) <= 0) return;
      const recipients = (s.recipients || []).map((r) => r.toLowerCase()).sort();
      const key = recipients.join('|') || mid;
      const lastOpened = stats.lastOpenedAt ? new Date(stats.lastOpenedAt).getTime() : 0;
      if (!groups[key]) {
        groups[key] = {
          id: key,
          recipients: s.recipients || [],
          subjects: new Set(),
          totalOpens: 0,
          messages: [],
          lastOpenedTs: lastOpened,
        };
      }
      groups[key].subjects.add(s.subject || '(No subject)');
      groups[key].totalOpens += stats.totalOpens || 0;
      groups[key].messages.push({ ...s, stats });
      if (lastOpened > groups[key].lastOpenedTs) {
        groups[key].lastOpenedTs = lastOpened;
      }
    });
    return Object.values(groups).map((g) => ({
      ...g,
      subjectLabel: Array.from(g.subjects).join(' | '),
      lastOpened: g.lastOpenedTs ? new Date(g.lastOpenedTs).toISOString() : null,
    }));
  }, [sent, trackingMap]);
  const filteredEngagedGroups = useMemo(() => {
    const query = String(engagedSearch || '').trim().toLowerCase();
    const minOpens = Number(engagedMinOpens) || 0;
    return engagedGroups.filter((group) => {
      if (minOpens && (group.totalOpens || 0) < minOpens) return false;
      if (!query) return true;
      const hay = [
        group.subjectLabel,
        (group.recipients || []).join(' '),
        group.lastOpened,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(query);
    });
  }, [engagedGroups, engagedSearch, engagedMinOpens]);
  const engagedPageCount = Math.max(1, Math.ceil(filteredEngagedGroups.length / 10));
  const subjectInsights = useMemo(() => {
    const value = compose.subject || '';
    const length = value.length;
    const lower = value.toLowerCase();
    const spamHints = ['free', 'urgent', 'act now', 'winner', 'guarantee', 'buy now', 'limited'];
    const hits = spamHints.filter((word) => lower.includes(word));
    return {
      length,
      tooShort: length > 0 && length < 4,
      tooLong: length > 70,
      spamHits: hits,
    };
  }, [compose.subject]);
  const bodyInsights = useMemo(() => {
    const text = htmlToText(compose.body || '');
    const words = text.trim().split(/\s+/).filter(Boolean);
    const linkCount = (compose.body || '').match(/<a\s/gi)?.length || 0;
    return {
      wordCount: words.length,
      linkCount,
      hasGreeting: /(hi|hello|dear)/i.test(text),
    };
  }, [compose.body]);
  const recipientStats = useMemo(() => {
    const list = compose.recipients || [];
    const invalid = list.filter((email) => !isValidEmail(email)).length;
    const domainCounts = {};
    list.forEach((email) => {
      const parts = String(email || '').split('@');
      if (parts.length < 2) return;
      const domain = parts[1].toLowerCase();
      if (!domain) return;
      domainCounts[domain] = (domainCounts[domain] || 0) + 1;
    });
    const topDomains = Object.entries(domainCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([domain, count]) => `${domain} (${count})`);
    return {
      total: list.length,
      invalid,
      topDomains,
    };
  }, [compose.recipients]);
  const syncUiPrefsToServer = async () => {
    if (!isAuthenticated) return;
    try {
      await adminApi.savePrefs(
        {
          activeTab,
          composePlanId,
          composeTemplateId,
          composeDraft: {
            accountId: compose.accountId || '',
            subject: compose.subject || '',
            body: compose.body || '',
            recipients: Array.isArray(compose.recipients) ? compose.recipients : [],
            manualBlock: compose.manualBlock || '',
          },
          notificationPrefs,
          filterUnread,
          engagedSearch,
          engagedMinOpens,
        },
        token
      );
    } catch (err) {
      setNotice({ open: true, type: 'error', message: 'Could not save email studio preferences to server.', scope: 'compose' });
    }
  };

  const loadPrefsFromServer = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await adminApi.loadPrefs(token);
      if (res?.prefs) {
        if (res.prefs.campaignPlan) setCampaignPlan((p) => ({ ...p, ...res.prefs.campaignPlan, perAccountLimits: res.prefs.campaignPlan?.perAccountLimits || {} }));
        if (Array.isArray(res.prefs.accountGroups)) setAccountGroups(res.prefs.accountGroups);
        if (typeof res.prefs.accountProviderFilter === 'string') setAccountProviderFilter(res.prefs.accountProviderFilter);
        if (typeof res.prefs.compactAccounts === 'boolean') setCompactAccounts(res.prefs.compactAccounts);
        if (typeof res.prefs.activeTab === 'number') setActiveTab(res.prefs.activeTab);
        if (typeof res.prefs.composePlanId === 'string') setComposePlanId(res.prefs.composePlanId);
        if (typeof res.prefs.composeTemplateId === 'string') setComposeTemplateId(res.prefs.composeTemplateId);
        if (res.prefs.composeDraft && typeof res.prefs.composeDraft === 'object') {
          const draft = res.prefs.composeDraft;
          setCompose((prev) => ({
            ...prev,
            accountId: typeof draft.accountId === 'string' ? draft.accountId : prev.accountId,
            subject: typeof draft.subject === 'string' ? draft.subject : prev.subject,
            body: typeof draft.body === 'string' ? draft.body : prev.body,
            recipients: Array.isArray(draft.recipients) ? draft.recipients : prev.recipients,
            manualBlock: typeof draft.manualBlock === 'string' ? draft.manualBlock : prev.manualBlock,
          }));
        }
        if (res.prefs.notificationPrefs && typeof res.prefs.notificationPrefs === 'object') {
          setNotificationPrefs((prev) => ({ ...prev, ...res.prefs.notificationPrefs }));
        }
        if (typeof res.prefs.filterUnread === 'boolean') setFilterUnread(res.prefs.filterUnread);
        if (typeof res.prefs.engagedSearch === 'string') setEngagedSearch(res.prefs.engagedSearch);
        if (typeof res.prefs.engagedMinOpens === 'number') setEngagedMinOpens(res.prefs.engagedMinOpens);
      }
    } catch (e) {
      const msg = e?.message || 'Could not load planner settings from server.';
      setNotice({ open: true, type: 'error', message: msg, scope: 'campaign' });
    }
  }, [isAuthenticated, token]);

  const carrierEmails = useMemo(
    () => extractCarrierEmails(carrierData),
    [carrierData]
  );

  const normalizeTemplateRecord = useCallback((template, fallback = {}) => {
    const id = template?.id || template?.template_id || fallback.id || '';
    return {
      id,
      name: template?.name ?? fallback.name ?? '',
      subject: template?.subject ?? fallback.subject ?? '',
      body: template?.body ?? fallback.body ?? '',
      category: template?.category ?? fallback.category ?? 'general',
      description: template?.description ?? fallback.description ?? '',
      tags: Array.isArray(template?.tags) ? template.tags : (fallback.tags || []),
      isFavorite: typeof template?.isFavorite === 'boolean'
        ? template.isFavorite
        : (typeof template?.is_favorite === 'boolean' ? template.is_favorite : !!fallback.isFavorite),
      uses: template?.uses ?? fallback.uses ?? 0,
      openRate: template?.openRate ?? template?.open_rate ?? fallback.openRate ?? 0,
      createdAt: template?.createdAt ?? template?.created_at ?? fallback.createdAt,
      updatedAt: template?.updatedAt ?? template?.updated_at ?? fallback.updatedAt,
    };
  }, []);

  const upsertTemplateRecord = useCallback((record) => {
    if (!record?.id) return;
    setCampaignTemplates((prev) => {
      const existingIdx = prev.findIndex((t) => t.id === record.id);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = { ...prev[existingIdx], ...record };
        return next;
      }
      return [record, ...prev].slice(0, 50);
    });
  }, []);

  const normalizePlanRecord = useCallback((plan, fallback = {}) => {
    const id = plan?.id || plan?.plan_id || fallback.id || '';
    const strategy = plan?.strategy || fallback.strategy || {};
    const meta = strategy?.meta || {};
    const cleanedStrategy = { ...strategy };
    if (cleanedStrategy && cleanedStrategy.meta) {
      delete cleanedStrategy.meta;
    }
    return {
      id,
      name: plan?.name ?? fallback.name ?? '',
      strategy: cleanedStrategy,
      templates: Array.isArray(plan?.templates) ? plan.templates : (fallback.templates || []),
      recipientRows: Array.isArray(plan?.recipientRows)
        ? plan.recipientRows
        : (Array.isArray(plan?.recipients) ? plan.recipients : (fallback.recipientRows || [])),
      recipientFileName: plan?.recipientFileName ?? plan?.recipient_file_name ?? fallback.recipientFileName ?? '',
      status: plan?.status ?? fallback.status ?? 'draft',
      description: plan?.description ?? meta.description ?? fallback.description ?? '',
      scheduledDate: plan?.scheduledDate ?? meta.scheduledDate ?? fallback.scheduledDate ?? null,
      timezone: plan?.timezone ?? meta.timezone ?? fallback.timezone ?? 'UTC',
      segmentType: plan?.segmentType ?? meta.segmentType ?? fallback.segmentType ?? 'all',
      sendingFrequency: plan?.sendingFrequency ?? meta.sendingFrequency ?? fallback.sendingFrequency ?? 'immediate',
      recipientLimit: plan?.recipientLimit ?? meta.recipientLimit ?? fallback.recipientLimit ?? '',
      createdAt: plan?.createdAt ?? plan?.created_at ?? fallback.createdAt,
      updatedAt: plan?.updatedAt ?? plan?.updated_at ?? fallback.updatedAt,
    };
  }, []);

  const upsertPlanRecord = useCallback((record) => {
    if (!record?.id) return;
    setSavedPlans((prev) => {
      const existingIdx = prev.findIndex((p) => p.id === record.id);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = { ...prev[existingIdx], ...record };
        return next;
      }
      return [record, ...prev].slice(0, 20);
    });
  }, []);

  // Auto-sync email workspace data to database
 

  useEffect(() => {
    setCompose((prev) => ({ ...prev, accountId: selectedAccountId }));
  }, [selectedAccountId]);

  useEffect(() => {
    if (isAuthenticated) {
      // Reset to avoid leaking another user's data while loading
      setCampaignTemplates([]);
      setCampaignPlan(defaultCampaignPlan);
      setSavedPlans([]);
      setAccountGroups([]);
      setAccountProviderFilter('');
      setCompactAccounts(true);
      setPlannerLoaded(false);
      loadPrefsFromServer().finally(() => setPlannerLoaded(true));
    } else {
      setCampaignTemplates([]);
      setCampaignPlan(defaultCampaignPlan);
      setSavedPlans([]);
      setAccountGroups([]);
      setAccountProviderFilter('');
      setCompactAccounts(true);
      setPlannerLoaded(true);
    }
  }, [isAuthenticated, token, loadPrefsFromServer]);

  // Database synchronization removed
  // useEffect(() => {
  //   if (token) {
  //     // databaseService initialization removed
  //   }
  // }, [token]);

  // Load email accounts from database - REMOVED (database functionality disabled)
  // useEffect(() => {
  //   if (!isAuthenticated || !token) return;
  //   // Database loading code removed
  // }, [isAuthenticated, token]);

  // Load templates, plans, groups and campaigns from database - REMOVED (database functionality disabled)
  // useEffect(() => {
  //   if (!isAuthenticated || !token) return;
  //   // Database loading code removed
  // }, [isAuthenticated, token]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setCampaignTemplates([]);
      return;
    }
    let cancelled = false;
    const loadTemplates = async () => {
      try {
        const response = await templateApi.listTemplates({}, token);
        if (cancelled) return;
        const templates = Array.isArray(response?.templates) ? response.templates : [];
        setCampaignTemplates(templates.map((t) => normalizeTemplateRecord(t)));
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof TemplateApiError
          ? error.message
          : 'Failed to load templates from database.';
        setNotice({ open: true, type: 'error', message, scope: 'campaign' });
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, normalizeTemplateRecord]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setSavedPlans([]);
      return;
    }
    let cancelled = false;
    const loadPlans = async () => {
      try {
        const res = await fetch(apiUrl('/api/campaign_plans.php?action=list'), {
          headers: authHeaders,
          credentials: 'include',
        });
        const payload = await res.json();
        if (!res.ok) {
          throw new Error(payload?.error || 'Failed to load plans');
        }
        if (cancelled) return;
        const plans = Array.isArray(payload?.plans) ? payload.plans : [];
        setSavedPlans(plans.map((plan) => normalizePlanRecord(plan)));
      } catch (error) {
        if (cancelled) return;
        setNotice({
          open: true,
          type: 'error',
          message: error?.message || 'Failed to load plans from database.',
          scope: 'campaign',
        });
      }
    };
    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, apiUrl, authHeaders, normalizePlanRecord]);

  useEffect(() => {
    // Allow first-sync skip per user session
    initialSyncSkipped.current = false;
    uiPrefsSyncSkipped.current = false;
  }, [token]);

  useEffect(() => {
    setSeenPage(1);
  }, [engagedSearch, engagedMinOpens]);

  useEffect(() => {
    if (!isAuthenticated || !plannerLoaded) return;
    if (!uiPrefsSyncSkipped.current) {
      uiPrefsSyncSkipped.current = true; // avoid spurious save on initial load
      return;
    }
    if (uiPrefsTimer.current) clearTimeout(uiPrefsTimer.current);
    uiPrefsTimer.current = setTimeout(() => {
      syncUiPrefsToServer();
    }, 800);
    return () => {
      if (uiPrefsTimer.current) clearTimeout(uiPrefsTimer.current);
    };
  }, [activeTab, composePlanId, composeTemplateId, compose, notificationPrefs, filterUnread, engagedSearch, engagedMinOpens, isAuthenticated, plannerLoaded]);

  useEffect(() => {
    // Auto-refresh inbox every 60s while on the unified inbox tab
    if (activeTab === 3) {
      fetchInbox().catch(() => {});
    }
  }, [activeTab, fetchInbox]);

  const setComposeBody = useCallback((html) => {
    setCompose((prev) => ({ ...prev, body: html || '' }));
  }, []);


  const playChime = useCallback(() => {
    if (!notificationPrefs.sound) return;
    try {
      // Soft bell made with Web Audio (two quick tones with fade-out)
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.exponentialRampToValueAtTime(0.25, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);

      const tone = (freq, startOffset = 0, dur = 0.35) => {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, now + startOffset);
        osc.connect(gain);
        osc.start(now + startOffset);
        osc.stop(now + startOffset + dur);
      };

      tone(880, 0, 0.35); // A5
      tone(1175, 0.12, 0.3); // D6 chime

      gain.connect(ctx.destination);
    } catch {
      // Fallback to a tiny inline bell sample
      try {
        const audio = new Audio(
          'data:audio/ogg;base64,T2dnUwACAAAAAAAAAACBP5weAAAAAN+brS8BHgF2b3JiaXMAAAAAAkSsAAAAAAAAgD+coAAAAAAAAPoAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAFYXQGPhZGVhbAIAAAAKAAAADAAAAGRhdGEAAAABAAABAgBQaW5rAAA='
        );
        audio.play().catch(() => {});
      } catch {
        /* ignore */
      }
    }
  }, [notificationPrefs.sound]);

  const triggerNotification = useCallback(
    (title, body) => {
      playChime();
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission === 'granted') {
        new Notification(title, { body });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((perm) => {
          if (perm === 'granted') {
            new Notification(title, { body });
          }
        });
      }
    },
    [playChime]
  );

  useEffect(() => {
    const unreadCount = inbox.filter((m) => m.status === 'unread').length;
    const readNow = new Set(inbox.filter((m) => m.status !== 'unread').map((m) => m.id));
    const totalOpens = trackingTotals?.totalOpens || 0;

    if (!notificationsInitializedRef.current) {
      prevUnreadCount.current = unreadCount;
      prevReadIds.current = readNow;
      prevTotalOpens.current = totalOpens;
      notificationsInitializedRef.current = true;
      return;
    }

    if (notificationPrefs.newEmail && unreadCount > prevUnreadCount.current) {
      const diff = unreadCount - prevUnreadCount.current;
      const msg = `?? ${diff} new message${diff > 1 ? 's' : ''} in unified inbox`;
      triggerNotification('New email received', msg);
      setFooterNotice({ open: true, message: msg });
    }
    prevUnreadCount.current = unreadCount;

    if (notificationPrefs.seen) {
      for (const id of readNow) {
        if (!prevReadIds.current.has(id)) {
          const msg = '?? A message was marked as read.';
          triggerNotification('Email read', msg);
          setFooterNotice({ open: true, message: msg });
          break;
        }
      }
    }
    prevReadIds.current = readNow;

    if (notificationPrefs.opens && totalOpens > prevTotalOpens.current) {
      const diff = totalOpens - prevTotalOpens.current;
      const msg = `?? ${diff} email${diff > 1 ? 's' : ''} were opened.`;
      triggerNotification('Email opened', msg);
      setFooterNotice({ open: true, message: msg });
    }
    prevTotalOpens.current = totalOpens;
  }, [inbox, notificationPrefs, triggerNotification, trackingTotals]);

  // Always poll inbox every 60s, even when not on the tab (lightweight)
  useEffect(() => {
    fetchInbox().catch(() => {});
    const id = setInterval(() => {
      fetchInbox().catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [fetchInbox]);

  // Poll tracking totals every 60s for seen-email notifications
  useEffect(() => {
    const run = () => {
      fetchTrackingTotals()
        .then((stats) => setTrackingTotals(stats))
        .catch(() => {});
    };
    run();
    const id = setInterval(run, 60000);
    return () => clearInterval(id);
  }, [fetchTrackingTotals]);

  // Light-weight md5 for gravatar (adapted for small payloads)
  const md5 = useCallback((str = '') => {
    const utf8 = new TextEncoder().encode(str);
    const rhex = (n) => {
      const s = '0123456789abcdef';
      return s[(n >> 4) & 0x0f] + s[n & 0x0f];
    };
    const add = (x, y) => (((x & 0xffff) + (y & 0xffff)) | 0) + ((((x >> 16) + (y >> 16)) & 0xffff) << 16);
    const rol = (x, c) => (x << c) | (x >>> (32 - c));
    const cmn = (q, a, b, x, s, t) => add(rol(add(add(a, q), add(x, t)), s), b);
    const ff = (a, b, c, d, x, s, t) => cmn((b & c) | (~b & d), a, b, x, s, t);
    const gg = (a, b, c, d, x, s, t) => cmn((b & d) | (c & ~d), a, b, x, s, t);
    const hh = (a, b, c, d, x, s, t) => cmn(b ^ c ^ d, a, b, x, s, t);
    const ii = (a, b, c, d, x, s, t) => cmn(c ^ (b | ~d), a, b, x, s, t);
    const data = Array.from(utf8);
    const origLen = data.length * 8;
    data.push(0x80);
    while ((data.length % 64) !== 56) data.push(0);
    for (let i = 0; i < 8; i++) data.push((origLen >>> (8 * i)) & 0xff);
    const words = [];
    for (let i = 0; i < data.length; i += 4) {
      words.push(data[i] | (data[i + 1] << 8) | (data[i + 2] << 16) | (data[i + 3] << 24));
    }
    let a = 1732584193;
    let b = -271733879;
    let c = -1732584194;
    let d = 271733878;
    for (let i = 0; i < words.length; i += 16) {
      const [oa, ob, oc, od] = [a, b, c, d];
      a = ff(a, b, c, d, words[i], 7, -680876936);
      d = ff(d, a, b, c, words[i + 1], 12, -389564586);
      c = ff(c, d, a, b, words[i + 2], 17, 606105819);
      b = ff(b, c, d, a, words[i + 3], 22, -1044525330);
      a = ff(a, b, c, d, words[i + 4], 7, -176418897);
      d = ff(d, a, b, c, words[i + 5], 12, 1200080426);
      c = ff(c, d, a, b, words[i + 6], 17, -1473231341);
      b = ff(b, c, d, a, words[i + 7], 22, -45705983);
      a = ff(a, b, c, d, words[i + 8], 7, 1770035416);
      d = ff(d, a, b, c, words[i + 9], 12, -1958414417);
      c = ff(c, d, a, b, words[i + 10], 17, -42063);
      b = ff(b, c, d, a, words[i + 11], 22, -1990404162);
      a = ff(a, b, c, d, words[i + 12], 7, 1804603682);
      d = ff(d, a, b, c, words[i + 13], 12, -40341101);
      c = ff(c, d, a, b, words[i + 14], 17, -1502002290);
      b = ff(b, c, d, a, words[i + 15], 22, 1236535329);
      a = gg(a, b, c, d, words[i + 1], 5, -165796510);
      d = gg(d, a, b, c, words[i + 6], 9, -1069501632);
      c = gg(c, d, a, b, words[i + 11], 14, 643717713);
      b = gg(b, c, d, a, words[i], 20, -373897302);
      a = gg(a, b, c, d, words[i + 5], 5, -701558691);
      d = gg(d, a, b, c, words[i + 10], 9, 38016083);
      c = gg(c, d, a, b, words[i + 15], 14, -660478335);
      b = gg(b, c, d, a, words[i + 4], 20, -405537848);
      a = gg(a, b, c, d, words[i + 9], 5, 568446438);
      d = gg(d, a, b, c, words[i + 14], 9, -1019803690);
      c = gg(c, d, a, b, words[i + 3], 14, -187363961);
      b = gg(b, c, d, a, words[i + 8], 20, 1163531501);
      a = gg(a, b, c, d, words[i + 13], 5, -1444681467);
      d = gg(d, a, b, c, words[i + 2], 9, -51403784);
      c = gg(c, d, a, b, words[i + 7], 14, 1735328473);
      b = gg(b, c, d, a, words[i + 12], 20, -1926607734);
      a = hh(a, b, c, d, words[i + 5], 4, -378558);
      d = hh(d, a, b, c, words[i + 8], 11, -2022574463);
      c = hh(c, d, a, b, words[i + 11], 16, 1839030562);
      b = hh(b, c, d, a, words[i + 14], 23, -35309556);
      a = hh(a, b, c, d, words[i + 1], 4, -1530992060);
      d = hh(d, a, b, c, words[i + 4], 11, 1272893353);
      c = hh(c, d, a, b, words[i + 7], 16, -155497632);
      b = hh(b, c, d, a, words[i + 10], 23, -1094730640);
      a = hh(a, b, c, d, words[i + 13], 4, 681279174);
      d = hh(d, a, b, c, words[i], 11, -358537222);
      c = hh(c, d, a, b, words[i + 3], 16, -722521979);
      b = hh(b, c, d, a, words[i + 6], 23, 76029189);
      a = hh(a, b, c, d, words[i + 9], 4, -640364487);
      d = hh(d, a, b, c, words[i + 12], 11, -421815835);
      c = hh(c, d, a, b, words[i + 15], 16, 530742520);
      b = hh(b, c, d, a, words[i + 2], 23, -995338651);
      a = ii(a, b, c, d, words[i], 6, -198630844);
      d = ii(d, a, b, c, words[i + 7], 10, 1126891415);
      c = ii(c, d, a, b, words[i + 14], 15, -1416354905);
      b = ii(b, c, d, a, words[i + 5], 21, -57434055);
      a = ii(a, b, c, d, words[i + 12], 6, 1700485571);
      d = ii(d, a, b, c, words[i + 3], 10, -1894986606);
      c = ii(c, d, a, b, words[i + 10], 15, -1051523);
      b = ii(b, c, d, a, words[i + 1], 21, -2054922799);
      a = ii(a, b, c, d, words[i + 8], 6, 1873313359);
      d = ii(d, a, b, c, words[i + 15], 10, -30611744);
      c = ii(c, d, a, b, words[i + 6], 15, -1560198380);
      b = ii(b, c, d, a, words[i + 13], 21, 1309151649);
      a = ii(a, b, c, d, words[i + 4], 6, -145523070);
      d = ii(d, a, b, c, words[i + 11], 10, -1120210379);
      c = ii(c, d, a, b, words[i + 2], 15, 718787259);
      b = ii(b, c, d, a, words[i + 9], 21, -343485551);
      a = add(a, oa);
      b = add(b, ob);
      c = add(c, oc);
      d = add(d, od);
    }
    const out = new Uint32Array([a, b, c, d]);
    const bytes = new Uint8Array(out.buffer);
    return Array.from(bytes).map((b) => rhex(b)).join('');
  }, []);

  const resolveAvatar = useCallback(
    (msg) => {
      if (msg?.avatarUrl || msg?.photo) return msg.avatarUrl || msg.photo;
      const email = String(msg?.from || '').trim().toLowerCase();
      if (!email || !email.includes('@')) return '';
      if (gravatarCache.current[email]) return gravatarCache.current[email];
      const hash = md5(email);
      const url = `https://www.gravatar.com/avatar/${hash}?d=identicon&s=64`;
      gravatarCache.current[email] = url;
      return url;
    },
    [md5]
  );

  useEffect(() => {
    return () => {
      if (campaignPollRef.current) clearInterval(campaignPollRef.current);
    };
  }, []);

  const applyTemplateToCompose = (tmpl) => {
    setCompose((prev) => ({
      ...prev,
      subject: tmpl.subject || '',
      body: tmpl.body || '',
    }));
    setComposeTemplateId(tmpl.id || '');
    setActiveTab(1);
    setNotice({ open: true, type: 'info', message: `Template "${tmpl.name}" applied to Compose.`, scope: 'compose' });
  };

  const savePlanToDatabase = async (record) => {
    if (!isAuthenticated || !token) {
      setNotice({ open: true, type: 'error', message: 'Please login to save plans.', scope: 'campaign' });
      return null;
    }
    const strategy = {
      ...(record.strategy || {}),
      meta: {
        description: record.description || '',
        scheduledDate: record.scheduledDate || null,
        timezone: record.timezone || 'UTC',
        segmentType: record.segmentType || 'all',
        sendingFrequency: record.sendingFrequency || 'immediate',
        recipientLimit: record.recipientLimit || '',
      },
    };
    const res = await fetch(apiUrl('/api/campaign_plans.php?action=save'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      credentials: 'include',
      body: JSON.stringify({
        id: record.id || '',
        name: record.name,
        strategy,
        templates: record.templates || [],
        recipients: record.recipientRows || [],
        recipientFileName: record.recipientFileName || '',
        status: record.status || 'draft',
      }),
    });
    const payload = await res.json();
    if (!res.ok || payload?.error) {
      throw new Error(payload?.error || 'Failed to save plan');
    }
    return payload?.plan ? normalizePlanRecord(payload.plan) : null;
  };

  const deletePlanFromDatabase = async (planId) => {
    if (!isAuthenticated || !token) {
      setNotice({ open: true, type: 'error', message: 'Please login to delete plans.', scope: 'campaign' });
      return;
    }
    const res = await fetch(apiUrl('/api/campaign_plans.php?action=delete'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      credentials: 'include',
      body: JSON.stringify({ id: planId }),
    });
    const payload = await res.json();
    if (!res.ok || payload?.error) {
      throw new Error(payload?.error || 'Failed to delete plan');
    }
  };

  const updatePlanStatus = async (planId, status) => {
    setSavedPlans((prev) =>
      prev.map((p) => (p.id === planId ? { ...p, status } : p))
    );
    const plan = savedPlans.find((p) => p.id === planId);
    if (!plan) return;
    const updated = { ...plan, status, updatedAt: new Date().toISOString() };
    try {
      const saved = await savePlanToDatabase(updated);
      if (saved) {
        upsertPlanRecord(saved);
      }
    } catch (error) {
      console.error('Error saving plan status:', error);
      setNotice({ open: true, type: 'error', message: 'Failed to save plan status.', scope: 'campaign' });
    }
  };

  const savePlanRunStats = async (planId, runStats) => {
    const plan = savedPlans.find((p) => p.id === planId);
    if (!plan) return;
    const updated = {
      ...plan,
      status: runStats?.status || plan.status,
      strategy: { ...(plan.strategy || {}), runStats: { ...(plan.strategy?.runStats || {}), ...runStats } },
      updatedAt: new Date().toISOString(),
    };
    try {
      const saved = await savePlanToDatabase(updated);
      if (saved) {
        upsertPlanRecord(saved);
      }
    } catch (error) {
      console.error('Error saving run stats:', error);
    }
  };

  const fetchCampaignStats = useCallback(
    async (planId) => {
      if (!isAuthenticated || !token || !planId) return null;
      const res = await fetch(apiUrl(`/api/campaign_stats.php?campaign_id=${encodeURIComponent(planId)}`), {
        headers: authHeaders,
        credentials: 'include',
      });
      const payload = await res.json();
      if (!res.ok || payload?.error || !payload?.ok) {
        throw new Error(payload?.error || 'Failed to load campaign stats');
      }
      return payload;
    },
    [apiUrl, authHeaders, isAuthenticated, token]
  );

  useEffect(() => {
    if (!isAuthenticated || !token || !savedPlans.length) return;
    let cancelled = false;
    (async () => {
      try {
        const statsEntries = await Promise.all(
          savedPlans.map(async (plan) => {
            if (!plan?.id) return null;
            const stats = await fetchCampaignStats(plan.id);
            return { planId: plan.id, stats };
          })
        );
        if (cancelled) return;
        setPlannerStats((prev) => {
          const next = { ...prev };
          statsEntries.forEach((entry) => {
            if (!entry?.planId || !entry?.stats) return;
            const existing = next[entry.planId] || {};
            next[entry.planId] = {
              ...existing,
              totalRecipients: Math.max(existing.totalRecipients || 0, entry.stats.sent || 0),
              opens: entry.stats.opens ?? existing.opens ?? 0,
              clicks: entry.stats.clicks ?? existing.clicks ?? 0,
              lastOpenedAt: entry.stats.lastOpenedAt ?? existing.lastOpenedAt ?? null,
            };
          });
          savedPlans.forEach((plan) => {
            const runStats = plan?.strategy?.runStats;
            if (!runStats) return;
            const existing = next[plan.id] || {};
            next[plan.id] = {
              ...existing,
              totalRecipients: Math.max(existing.totalRecipients || 0, runStats.totalRecipients || 0),
              sent: runStats.sent ?? existing.sent ?? 0,
              failed: runStats.failed ?? existing.failed ?? 0,
              failedRecipients: runStats.failedRecipients || existing.failedRecipients || [],
            };
          });
          return next;
        });
      } catch (error) {
        console.error('Failed to load campaign stats:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchCampaignStats, isAuthenticated, savedPlans, token]);

  const savePlan = async (name) => {
    if (!name.trim()) {
      setNotice({ open: true, type: 'error', message: 'Plan name is required.', scope: 'campaign' });
      return;
    }
    const record = normalizePlanRecord(
      {
        id: `plan-${Date.now()}`,
        name: name.trim(),
        strategy: { ...campaignPlan, perAccountLimits: campaignPlan.perAccountLimits || {} },
        templates: campaignTemplates,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    );
    upsertPlanRecord(record);
    setNotice({ open: true, type: 'success', message: 'Plan saved.', scope: 'campaign' });
    // Clear selections so the next plan starts fresh
    setCampaignPlan((prev) => ({
      ...prev,
      selectedTemplates: [],
      selectedAccounts: [],
      perAccountLimits: {},
    }));

    try {
      const saved = await savePlanToDatabase(record);
      if (saved) {
        upsertPlanRecord(saved);
      }
      setNotice({ open: true, type: 'success', message: 'Plan saved to database.', scope: 'campaign' });
    } catch (error) {
      console.error('Error saving plan:', error);
      setNotice({ open: true, type: 'error', message: 'Failed to save plan to database.', scope: 'campaign' });
    }
  };

  const applyPlan = (plan) => {
    setCampaignPlan({ ...defaultCampaignPlan, ...plan.strategy, perAccountLimits: plan.strategy?.perAccountLimits || {} });
    setNotice({ open: true, type: 'success', message: `Plan "${plan.name}" loaded.`, scope: 'campaign' });
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const findEmailForRow = (row) => {
    if (!row || typeof row !== 'object') return '';
    const keys = Object.keys(row);
    const emailKey =
      keys.find((k) => /email/i.test(k)) ||
      keys.find((k) => /e-mail/i.test(k)) ||
      keys.find((k) => /mail/i.test(k));
    if (!emailKey) return '';
    const value = row[emailKey];
    const emails = normalizeEmails(value || '');
    return emails[0] || '';
  };

  const countRecipientsInRows = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return 0;
    let count = 0;
    rows.forEach((row) => {
      const email = findEmailForRow(row);
      if (email) count += 1;
    });
    return count;
  };

  const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const applyMerge = (text, row) => {
    if (!text || !row) return text || '';
    let result = String(text);
    Object.entries(row).forEach(([key, value]) => {
      const token = new RegExp(`{{\\s*${escapeRegExp(key)}\\s*}}`, 'gi');
      result = result.replace(token, String(value ?? ''));
    });
    const nameRaw =
      row.first_name ||
      row.firstname ||
      row['first name'] ||
      row.first ||
      row.name ||
      '';
    if (nameRaw) {
      const first = String(nameRaw).split(/\s+/)[0];
      result = result.replace(/{{\s*first_name\s*}}/gi, first);
      result = result.replace(/{{\s*name\s*}}/gi, String(nameRaw));
    }
    return result;
  };

  const isNetworkSendError = (error) => {
    const msg = String(error?.message || '').toLowerCase();
    return msg.includes('failed to fetch')
      || msg.includes('networkerror')
      || msg.includes('cors');
  };

  const loadPlanToCompose = (plan) => {
    applyPlan(plan);
    const firstTmplId = plan.strategy?.selectedTemplates?.[0];
    if (firstTmplId) {
      const tmpl = campaignTemplates.find((t) => t.id === firstTmplId);
      if (tmpl) {
        applyTemplateToCompose(tmpl);
        setComposeTemplateId(firstTmplId);
      }
    }
    setActiveTab(1);
  };

  const handlePlanEdit = (plan) => {
    applyPlan(plan);
    const newName = prompt('Rename plan (optional):', plan.name);
    if (newName && newName.trim()) {
      const updated = { ...plan, name: newName.trim(), updatedAt: new Date().toISOString() };
      upsertPlanRecord(updated);
      savePlanToDatabase(updated).then((saved) => {
        if (saved) upsertPlanRecord(saved);
      }).catch((error) => {
        console.error('Error saving plan:', error);
        setNotice({ open: true, type: 'error', message: 'Failed to save plan to database.', scope: 'campaign' });
      });
    }
  };

  const startCampaignFromCompose = async () => {
    if (!compose.recipients || compose.recipients.length === 0) {
      setNotice({ open: true, type: 'error', message: 'Add recipients first.', scope: 'compose' });
      return;
    }
    const activePlan = composePlanId ? savedPlans.find((p) => p.id === composePlanId) : null;
    const strategy = activePlan?.strategy || campaignPlan;
    const strategyTemplateIds = strategy.selectedTemplates || [];
    let templatesPool = composeTemplateId
      ? campaignTemplates.filter((t) => t.id === composeTemplateId)
      : campaignTemplates.filter((t) => strategyTemplateIds.includes(t.id));
    // If the saved plan carries full template objects but no IDs matched current library, fall back to those
    if (!templatesPool.length && activePlan?.templates?.length) {
      templatesPool = activePlan.templates;
    }
    if (!templatesPool.length) {
      setNotice({ open: true, type: 'error', message: 'Select a template or choose a plan with templates.', scope: 'compose' });
      return;
    }
    const requestedSelection = (strategy.selectedAccounts || []).length > 0;
    const accountIdsForPlan = expandAccountSelection(strategy.selectedAccounts || []);
    let accountsPayloadBase = accountIdsForPlan.length
      ? accounts.filter((a) => accountIdsForPlan.includes(a.id))
      : accounts;
    // If nothing matched (e.g., plan selected but accounts filter empty), fall back to all available accounts
    if (!accountsPayloadBase.length && accounts.length) {
      accountsPayloadBase = accounts;
    }
    const accountsPayload = accountsPayloadBase.map((a) => ({
      ...a,
      dailyLimit: (strategy.perAccountLimits || {})[a.id] || a.dailyLimit || strategy.perAccountDaily,
    }));
    if (!accountsPayload.length) {
      setNotice({ open: true, type: 'error', message: 'Select an account.', scope: 'compose' });
      return;
    }
    if (requestedSelection && !accountIdsForPlan.length) {
      setNotice({ open: true, type: 'error', message: 'Selected group has no accounts.', scope: 'compose' });
      return;
    }
    setCampaignRun({ id: null, counts: {}, running: true });
    try {
      const apiPath = apiUrl('/api/campaign.php');
      if (!apiPath) {
        throw new Error('API base URL is missing. Set REACT_APP_API_BASE (or REACT_APP_API_BASE_URL) to your PHP host.');
      }
      const res = await fetch(apiPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
          body: JSON.stringify({
            action: 'create',
            name: `Campaign ${new Date().toLocaleString()}`,
            templates: templatesPool,
            accounts: accountsPayload,
            recipients: compose.recipients,
            settings: {
              minDelaySec: strategy.minDelaySec,
              maxDelaySec: strategy.maxDelaySec,
              perAccountDaily: strategy.perAccountDaily,
              rotateAccounts: strategy.rotateAccounts,
              shuffleTemplates: strategy.shuffleTemplates,
              trackOpens: campaignTrackOpens,
            },
          }),
        });
      const resClone = res.clone();
      let payload;
      try {
        payload = await res.json();
      } catch (e) {
        const text = await resClone.text();
        throw new Error(`Unexpected response from server (create): ${text.slice(0, 200)}`);
      }
      if (!res.ok || !payload?.ok) {
        const text = await resClone.text();
        throw new Error(payload?.error || text || 'Campaign create failed');
      }
      setCampaignRun({ id: payload.campaignId, counts: {}, running: true });
      setNotice({ open: true, type: 'success', message: 'Campaign started.', scope: 'compose' });
      if (isAuthenticated) {
        adminApi.logActivity({ type: 'email_send', source: 'campaign', metadata: { campaignId: payload.campaignId } }, token).catch(() => {});
      }
      const runRes = await fetch(apiPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ action: 'run', id: payload.campaignId, limit: 25 }),
      });
      if (!runRes.ok) {
        const runText = await runRes.text();
        // Log but continue polling so the user sees status and errors instead of a hard stop
        setNotice({
          open: true,
          type: 'error',
          message: runText || `Campaign run failed (status ${runRes.status})`,
          scope: 'compose',
        });
      }
      if (campaignPollRef.current) clearInterval(campaignPollRef.current);
      campaignPollRef.current = setInterval(async () => {
        try {
          // 1) Poll status
          const statRes = await fetch(apiUrl(`/api/campaign.php?action=status&id=${payload.campaignId}`), {
            headers: authHeaders,
            credentials: 'include',
          });
          let stat;
          try {
            stat = await statRes.json();
          } catch (e) {
            const txt = await statRes.text();
            throw new Error(`Unexpected response from server (status): ${txt.slice(0, 120)}`);
          }
          if (stat?.ok) {
            setCampaignRun((prev) => ({ ...prev, counts: stat.counts || {}, running: true, id: payload.campaignId }));
            const queued = stat.counts?.queued || 0;
            const sending = stat.counts?.sending || 0;
            if (queued + sending === 0) {
              clearInterval(campaignPollRef.current);
              setCampaignRun((prev) => ({ ...prev, running: false }));
              setNotice({ open: true, type: 'success', message: 'Campaign completed.', scope: 'compose' });
              return;
            }
            // 2) Trigger another run cycle to process newly due jobs
            await fetch(apiPath, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders },
              credentials: 'include',
              body: JSON.stringify({ action: 'run', id: payload.campaignId, limit: 25 }),
            });
          }
        } catch (e) {
          clearInterval(campaignPollRef.current);
          setCampaignRun((prev) => ({ ...prev, running: false }));
        }
      }, 4000);
    } catch (err) {
      setCampaignRun({ id: null, counts: {}, running: false });
      const msg =
        err?.message === 'Failed to fetch'
          ? 'Network error starting campaign. Check API base URL /api/campaign.php and CORS.'
          : err?.message || 'Campaign start failed';
      setNotice({ open: true, type: 'error', message: msg, scope: 'compose' });
    }
  };
  useEffect(() => {
    if (!activeMessageId && inbox.length > 0) {
      setActiveMessageId(inbox[0].id);
    }
  }, [activeMessageId, inbox]);

  useEffect(() => {
    if (!sent.length) return;
    fetchTrackingTotals()
      .then((stats) => setTrackingTotals(stats))
      .catch(() => {});
  }, [sent, fetchTrackingTotals]);

  useEffect(() => {
    const active = inbox.find((m) => m.id === activeMessageId);
    if (!active) return;
    const normalizeSubject = (str = '') => {
      let out = str.trim();
      while (/^(re:|fw:|fwd:)\s*/i.test(out)) {
        out = out.replace(/^(re:|fw:|fwd:)\s*/i, '').trim();
      }
      return out.toLowerCase();
    };
    const subjectKey = normalizeSubject(active.subject || '');
    const relatedSent = sent.filter(
      (s) =>
        s.parentId === active.id ||
        normalizeSubject(s.subject || '') === subjectKey
    );
    relatedSent.forEach((s) => {
      const mid = s.messageId || s.id;
      if (!mid || trackingMap[mid]) return;
      fetchTrackingStats(mid)
        .then((stats) => {
          setTrackingMap((prev) => ({ ...prev, [mid]: stats }));
        })
        .catch(() => {});
    });
  }, [activeMessageId, inbox, sent, trackingMap, fetchTrackingStats]);

  const refreshSeenStats = React.useCallback(async () => {
    if (!sent.length) return;
    setSeenLoading(true);
    try {
      const updates = {};
      for (const s of sent) {
        const mid = s.messageId || s.id;
        if (!mid) continue;
        try {
          const stats = await fetchTrackingStats(mid);
          updates[mid] = stats;
        } catch {
          // ignore individual failures
        }
      }
      if (Object.keys(updates).length) {
        setTrackingMap((prev) => ({ ...prev, ...updates }));
      }
    } finally {
      setSeenLoading(false);
    }
  }, [sent, fetchTrackingStats]);

  useEffect(() => {
    if (activeTab === 4) {
      refreshSeenStats().catch(() => {});
      setSeenPage(1);
    }
  }, [activeTab, refreshSeenStats]);

  const saveTemplate = async (payload = null, options = {}) => {
    const data = payload || templateForm;
    if (!data.name.trim() || !data.subject.trim() || !data.body.trim()) {
      setNotice({ open: true, type: 'error', message: 'Template name, subject, and body are required.', scope: 'campaign' });
      return;
    }
    const record = normalizeTemplateRecord(data, {
      id: data.id || `tmpl-${Date.now()}`,
      name: data.name.trim(),
      subject: data.subject.trim(),
      body: data.body.trim(),
      category: data.category || 'general',
      description: data.description || '',
      tags: Array.isArray(data.tags) ? data.tags : [],
      isFavorite: !!data.isFavorite,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    if (options.skipRemote) {
      upsertTemplateRecord(record);
      return;
    }

    if (!isAuthenticated || !token) {
      setNotice({ open: true, type: 'error', message: 'Please login to save templates.', scope: 'campaign' });
      return;
    }

    try {
      const response = await templateApi.saveTemplate(record, token);
      const saved = normalizeTemplateRecord(response?.template || record, {
        ...record,
        updatedAt: new Date().toISOString(),
      });
      upsertTemplateRecord(saved);
      setTemplateForm({ id: '', name: '', subject: '', body: '' });
      setNotice({ open: true, type: 'success', message: 'Template saved successfully.', scope: 'campaign' });
    } catch (error) {
      console.error('Error saving template:', error);
      const message = error instanceof TemplateApiError
        ? error.message
        : 'Failed to save template to database.';
      setNotice({ open: true, type: 'error', message, scope: 'campaign' });
    }
  };

  const deleteTemplate = async (id, options = {}) => {
    setCampaignTemplates((prev) => prev.filter((t) => t.id !== id));
    if (options.skipRemote) return;
    if (!isAuthenticated || !token) {
      setNotice({ open: true, type: 'error', message: 'Please login to delete templates.', scope: 'campaign' });
      return;
    }
    try {
      await templateApi.deleteTemplate(id, token);
      setNotice({ open: true, type: 'success', message: 'Template deleted.', scope: 'campaign' });
    } catch (error) {
      console.error('Error deleting template:', error);
      const message = error instanceof TemplateApiError
        ? error.message
        : 'Failed to delete template.';
      setNotice({ open: true, type: 'error', message, scope: 'campaign' });
    }
  };

  const buildPlanPreview = () => {
    const templates = campaignTemplates.filter((t) => campaignPlan.selectedTemplates.includes(t.id));
    const acctIds = expandAccountSelection(campaignPlan.selectedAccounts);
    const poolIds = acctIds.length ? acctIds : accounts.map((a) => a.id);
    if (!templates.length || !poolIds.length) {
      setNotice({ open: true, type: 'error', message: 'Select at least one template and one account.', scope: 'campaign' });
      return;
    }
    const items = [];
    for (let i = 0; i < 10; i++) {
      const acctId = poolIds[campaignPlan.rotateAccounts ? i % poolIds.length : 0];
      const tmpl = campaignPlan.shuffleTemplates
        ? templates[Math.floor(Math.random() * templates.length)]
        : templates[i % templates.length];
      const delay =
        campaignPlan.minDelaySec +
        Math.floor(Math.random() * Math.max(1, campaignPlan.maxDelaySec - campaignPlan.minDelaySec + 1));
      items.push({
        idx: i + 1,
        account: accounts.find((a) => a.id === acctId)?.label || acctId,
        template: tmpl.name,
        delay,
      });
    }
    // preview removed in simplified planner
  };

  const cleanBody = (text = '') => {
    if (showRaw) return text;
    // Convert simple HTML bodies to text before stripping quoted blocks
    let plain = text;
    if (/<[a-z][\s\S]*>/i.test(text)) {
      const div = document.createElement('div');
      div.innerHTML = text;
      plain = div.innerText || div.textContent || text;
    }

    const lines = String(plain).split(/\r?\n/);
    const filtered = [];
    for (const line of lines) {
      const trimmed = line.trim();
      // stop at typical reply headers
      if (/^on .*wrote:?$/i.test(trimmed)) break;
      if (/^-{2,}\s*original message\s*-{2,}$/i.test(trimmed)) break;
      if (/^>/.test(trimmed)) continue; // skip quoted lines
      if (/^from: /i.test(trimmed)) continue;
      if (/^sent: /i.test(trimmed)) continue;
      if (/^subject: /i.test(trimmed)) continue;
      filtered.push(line);
    }
    return filtered.join('\n').trim();
  };

  function htmlToText(html = '') {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.innerText || temp.textContent || '';
  }
  const insertMergeTag = useCallback((token) => {
    if (!token) return;
    setCompose((prev) => ({ ...prev, body: `${prev.body || ''}${token}` }));
  }, []);

  const insertTemplateMarkup = useCallback((before, after = '') => {
    const textarea = templateBodyRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateForm.body || '';
    const selectedText = text.substring(start, end) || 'text';
    const newText =
      text.substring(0, start) + before + selectedText + after + text.substring(end);
    setTemplateForm((prev) => ({ ...prev, body: newText }));
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selectedText.length
      );
    }, 0);
  }, [templateForm.body]);

  const applyTemplateFormat = useCallback(
    (format) => {
      switch (format) {
        case 'bold':
          insertTemplateMarkup('**', '**');
          break;
        case 'italic':
          insertTemplateMarkup('*', '*');
          break;
        case 'underline':
          insertTemplateMarkup('<u>', '</u>');
          break;
        case 'code':
          insertTemplateMarkup('```', '```');
          break;
        case 'quote':
          insertTemplateMarkup('> ', '');
          break;
        case 'link':
          insertTemplateMarkup('[Link](', ')');
          break;
        case 'heading':
          insertTemplateMarkup('## ', '');
          break;
        case 'bullet':
          insertTemplateMarkup('- ', '');
          break;
        case 'number':
          insertTemplateMarkup('1. ', '');
          break;
        case 'clear':
          setTemplateForm((prev) => ({ ...prev, body: '' }));
          break;
        default:
          break;
      }
    },
    [insertTemplateMarkup]
  );

  const handleEmailChange = (email) => {
    setAccountForm((prev) => ({ ...prev, fromEmail: email.trim() }));
    
    // Auto-detect provider
    const detectedProvider = detectProviderByEmail(email);
    setSelectedProviderTemplate(detectedProvider);
    
    // Auto-fill SMTP/IMAP settings if provider detected
    if (detectedProvider) {
      const template = applyProviderTemplate(email, detectedProvider);
      setAccountForm((prev) => ({
        ...prev,
        ...template,
      }));
    }
  };

  const handleProviderSelect = (provider) => {
    setSelectedProviderTemplate(provider);
    
    // Apply provider template
    const template = applyProviderTemplate(accountForm.fromEmail, provider);
    if (template) {
      setAccountForm((prev) => ({
        ...prev,
        ...template,
      }));
    }
    
    setShowProviderGuide(true);
  };

  const handleAccountSave = async () => {
    setSavingAccount(true);
    try {
      setAccountError('');
      const effectiveForm = { ...accountForm };
      if (!effectiveForm.fromEmail && effectiveForm.username) {
        effectiveForm.fromEmail = effectiveForm.username;
        setAccountForm((prev) => ({ ...prev, fromEmail: effectiveForm.fromEmail }));
      }
      if (!effectiveForm.fromEmail || !effectiveForm.host) {
        setAccountError('From email and SMTP host are required.');
        return false;
      }
      if (editingId) {
        const updated = { ...effectiveForm, id: editingId };
        updateAccount(editingId, effectiveForm);
        setAccountMessage('Account updated.');
      } else {
        const record = addAccount(effectiveForm);
        setEditingId(record.id);
        setAccountMessage('Account added.');
      }
      setNotice({ open: true, type: 'success', message: 'Account saved successfully!', scope: 'accounts' });
      return true;
    } catch (err) {
      const msg = err?.message || 'Failed to save account';
      setAccountError(msg);
      setNotice({ open: true, type: 'error', message: msg, scope: 'accounts' });
      console.error('[EmailWorkspace] Account save failed', err);
      return false;
    } finally {
      setSavingAccount(false);
    }
  };

  const handleAccountDelete = async (accountId) => {
    try {
      // Delete from local state
      deleteAccount(accountId);
      setNotice({ open: true, type: 'success', message: 'Account deleted successfully!', scope: 'accounts' });
    } catch (err) {
      const msg = err?.message || 'Delete failed';
      setNotice({ open: true, type: 'error', message: msg, scope: 'accounts' });
      console.error('[EmailWorkspace] Account delete failed', err);
    }
  };

  const handleAccountTest = async () => {
    setTestingAccount(true);
    try {
      const res = await testAccount(
        editingId
          ? { ...accountForm, id: editingId }
          : { ...accountForm, id: 'draft' }
      );
      setAccountMessage(res.message);
      setAccountError('');
      setTestPassed(true);
      setNotice({ open: true, type: 'success', message: res.message, scope: 'accounts' });
    } catch (err) {
      setAccountError(err.message || 'Unable to test connection');
      setTestPassed(false);
      setNotice({ open: true, type: 'error', message: err.message || 'Test failed', scope: 'accounts' });
    } finally {
      setTestingAccount(false);
    }
  };

  const handleInlineAccountTest = async (acc) => {
    try {
      const res = await testAccount(acc);
      const msg = res?.message || 'Connection successful';
      if (acc?.id) {
        updateAccount(acc.id, { status: 'connected', lastTestAt: new Date().toISOString() });
      }
      setNotice({ open: true, type: 'success', message: msg, scope: 'accounts' });
    } catch (err) {
      if (acc?.id) {
        updateAccount(acc.id, {
          status: 'failed',
          lastTestAt: new Date().toISOString(),
          lastTestError: err.message || 'Test failed',
        });
      }
      setNotice({ open: true, type: 'error', message: err.message || 'Test failed', scope: 'accounts' });
    }
  };

  const launchPlannerCampaign = async (planId) => {
    const plan = savedPlans.find((p) => p.id === planId);
    if (!plan) return;
    const templatesPool = (plan.templates && plan.templates.length)
      ? plan.templates
      : campaignTemplates.filter((t) => (plan.strategy?.selectedTemplates || []).includes(t.id));
    if (!templatesPool.length) {
      setNotice({ open: true, type: 'error', message: 'Select at least one template for this campaign.', scope: 'campaign' });
      return;
    }
    const rows = Array.isArray(plan.recipientRows) ? plan.recipientRows : [];
    if (!rows.length) {
      setNotice({ open: true, type: 'error', message: 'Import a recipient file first.', scope: 'campaign' });
      return;
    }
    const accountIds = expandAccountSelection(plan.strategy?.selectedAccounts || []);
    const accountPool = accounts.filter((a) => accountIds.includes(a.id));
    if (!accountPool.length) {
      setNotice({ open: true, type: 'error', message: 'Select at least one account.', scope: 'campaign' });
      return;
    }

    updatePlanStatus(planId, 'running');

    const totalRecipients = countRecipientsInRows(rows);
    setPlannerStats((prev) => ({
      ...prev,
      [planId]: {
        totalRecipients,
        sent: 0,
        failed: 0,
        failedRecipients: [],
        trackingWarnings: [],
      },
    }));

    const strategy = plan.strategy || defaultCampaignPlan;
    const minDelayBase = Math.max(0, Number(strategy.minDelaySec || 0));
    const minDelay = strategy.enforceMinDelay ? Math.max(minDelayBase, 15) : minDelayBase;
    const maxDelay = Math.max(minDelay, Number(strategy.maxDelaySec || 0));
    const jitterSec = Math.max(0, Number(strategy.jitterSec || 0));
    const batchPauseEvery = Math.max(0, Number(strategy.batchPauseEvery || 0));
    const batchPauseSec = Math.max(0, Number(strategy.batchPauseSec || 0));
    const maxPerAccountPerHour = Math.max(0, Number(strategy.maxPerAccountPerHour || 0));
    const maxFailures = Math.max(0, Number(strategy.maxFailures || 0));
    const trackOpens = strategy.trackOpens !== false;
    const useRotation = strategy.rotateAccounts !== false;
    const sentByAccount = new Map();

    const pickAccount = async (index) => {
      if (!accountPool.length) return null;
      const poolSize = accountPool.length;
      const startIdx = useRotation ? index % poolSize : 0;
      const now = Date.now();
      if (!maxPerAccountPerHour) {
        return accountPool[startIdx];
      }
      let nextWaitMs = null;
      for (let offset = 0; offset < poolSize; offset += 1) {
        const idx = (startIdx + offset) % poolSize;
        const acct = accountPool[idx];
        const history = sentByAccount.get(acct.id) || [];
        const recent = history.filter((ts) => ts > now - 3600 * 1000);
        if (recent.length < maxPerAccountPerHour) {
          sentByAccount.set(acct.id, recent);
          return acct;
        }
        const earliest = Math.min(...recent);
        const waitMs = Math.max(0, earliest + 3600 * 1000 - now);
        if (nextWaitMs === null || waitMs < nextWaitMs) {
          nextWaitMs = waitMs;
        }
      }
      if (nextWaitMs && nextWaitMs > 0) {
        await sleep(nextWaitMs);
      }
      return accountPool[startIdx];
    };

    let sentCount = 0;
    let failedCount = 0;
    let failedRecipients = [];
    let trackingWarnings = [];
    let stoppedEarly = false;

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const email = findEmailForRow(row);
      if (!email) {
        failedCount += 1;
        failedRecipients = [
          ...failedRecipients,
          { email: '(missing email)', error: 'No email found in row' },
        ];
        setPlannerStats((prev) => ({
          ...prev,
          [planId]: {
            ...prev[planId],
            totalRecipients,
            sent: sentCount,
            failed: failedCount,
            failedRecipients,
          },
        }));
        continue;
      }

      const account = await pickAccount(i);
      if (!account) {
        failedCount += 1;
        failedRecipients = [
          ...failedRecipients,
          { email, error: 'No account available' },
        ];
        setPlannerStats((prev) => ({
          ...prev,
          [planId]: {
            ...prev[planId],
            totalRecipients,
            sent: sentCount,
            failed: failedCount,
            failedRecipients,
          },
        }));
        continue;
      }
        const template = strategy.shuffleTemplates
        ? templatesPool[Math.floor(Math.random() * templatesPool.length)]
        : templatesPool[i % templatesPool.length];
      const subject = applyMerge(template.subject || '', row);
      const bodyHtml = applyMerge(template.body || '', row);
      const bodyText = htmlToText(bodyHtml || '');

      try {
        const record = await sendEmail({
          subject,
          body: bodyText,
          bodyText,
          bodyHtml,
          recipients: [email],
          accountId: account.id,
          campaignId: planId,
          trackOpens,
        });
        if (record?.trackingError) {
          trackingWarnings = [
            ...trackingWarnings,
            { email, error: record.trackingError },
          ];
        }
        sentCount += 1;
        if (maxPerAccountPerHour) {
          const history = sentByAccount.get(account.id) || [];
          history.push(Date.now());
          sentByAccount.set(account.id, history);
        }
      } catch (err) {
        if (isNetworkSendError(err)) {
          sentCount += 1;
          trackingWarnings = [
            ...trackingWarnings,
            { email, error: err?.message || 'Response blocked by browser' },
          ];
        } else {
          failedCount += 1;
          failedRecipients = [
            ...failedRecipients,
            { email, error: err?.message || 'Send failed' },
          ];
        }
      }

      setPlannerStats((prev) => ({
        ...prev,
        [planId]: {
          ...prev[planId],
          totalRecipients,
          sent: sentCount,
          failed: failedCount,
          failedRecipients,
          trackingWarnings,
        },
      }));

      if (maxFailures && failedCount >= maxFailures) {
        updatePlanStatus(planId, 'failed');
        setNotice({
          open: true,
          type: 'error',
          message: `Campaign stopped after ${failedCount} failures.`,
          scope: 'campaign',
        });
        stoppedEarly = true;
        await savePlanRunStats(planId, {
          totalRecipients,
          sent: sentCount,
          failed: failedCount,
          failedRecipients,
          trackingWarnings,
          lastRunAt: new Date().toISOString(),
          status: 'failed',
        });
        break;
      }

      const randomDelay =
        minDelay + Math.floor(Math.random() * Math.max(1, maxDelay - minDelay + 1));
      const delayMs = (randomDelay + (jitterSec ? Math.floor(Math.random() * (jitterSec + 1)) : 0)) * 1000;
      if (delayMs > 0) {
        await sleep(delayMs);
      }

      if (batchPauseEvery && batchPauseSec && (i + 1) % batchPauseEvery === 0) {
        await sleep(batchPauseSec * 1000);
      }
    }

    if (stoppedEarly) {
      return;
    }
    updatePlanStatus(planId, 'completed');
    await savePlanRunStats(planId, {
      totalRecipients,
      sent: sentCount,
      failed: failedCount,
      failedRecipients,
      trackingWarnings,
      lastRunAt: new Date().toISOString(),
      status: 'completed',
    });
    setNotice({
      open: true,
      type: failedCount ? 'warning' : 'success',
      message: `Campaign finished. Sent: ${sentCount}, Failed: ${failedCount}.`,
      scope: 'campaign',
    });
  };
  const handleBulkAccountTest = async () => {
    if (bulkTesting || visibleAccounts.length === 0) return;
    setBulkTesting(true);
    let success = 0;
    let failed = 0;
    for (const acc of visibleAccounts) {
      try {
        await testAccount(acc);
        updateAccount(acc.id, { status: 'connected', lastTestAt: new Date().toISOString() });
        success += 1;
      } catch (err) {
        updateAccount(acc.id, {
          status: 'failed',
          lastTestAt: new Date().toISOString(),
          lastTestError: err.message || 'Test failed',
        });
        failed += 1;
      }
    }
    setBulkTestSummary({ success, failed });
    const msg = `Bulk test finished. Success: ${success}, Failed: ${failed}.`;
    setNotice({ open: true, type: failed ? 'warning' : 'success', message: msg, scope: 'accounts' });
    setBulkTesting(false);
  };

  const handleAccountEdit = (acc) => {
    setAccountForm({
      label: acc.label || '',
      provider: acc.provider || 'SMTP',
      fromName: acc.fromName || '',
      fromEmail: acc.fromEmail || '',
      host: acc.host || '',
      port: acc.port || 587,
      secure: acc.secure !== false,
      username: acc.username || '',
      password: acc.password || '',
      imapHost: acc.imapHost || '',
      imapPort: acc.imapPort || 993,
      imapSecure: acc.imapSecure !== false,
      mailbox: acc.mailbox || 'INBOX',
      popHost: acc.popHost || '',
      popPort: acc.popPort || 995,
      popSecure: acc.popSecure !== false,
      dailyLimit: acc.dailyLimit || 0,
    });
    setEditingId(acc.id);
    setSelectedAccountId(acc.id);
    setAccountMessage('');
    setAccountError('');
    setAccountModalOpen(true);
  };

  const handleRecipientsFromManual = () => {
    if (!compose.manualBlock.trim()) return;
    setCompose((prev) => ({
      ...prev,
      recipients: mergeRecipients(prev.recipients, prev.manualBlock.split(/[\n,;]+/)),
      manualBlock: '',
    }));
  };

  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadError('');
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (!rows.length) {
        throw new Error('No rows found in the uploaded file.');
      }
      const headers = (rows[0] || []).map((h, idx) => {
        const label = String(h || '').trim();
        return label || `Column ${idx + 1}`;
      });
      const dataRows = rows.slice(1).filter((row) => row.some((cell) => String(cell || '').trim() !== ''));
      const mappedRows = dataRows.map((row) => {
        const record = {};
        headers.forEach((key, idx) => {
          record[key] = row[idx] ?? '';
        });
        return record;
      });
      const emailKey =
        headers.find((key) => /email/i.test(key)) ||
        headers.find((key) => /e-mail/i.test(key)) ||
        headers.find((key) => /mail/i.test(key));
      const extracted = [];
      const byEmail = {};
      mappedRows.forEach((row) => {
        if (emailKey && row[emailKey]) {
          const emails = normalizeEmails(row[emailKey]);
          emails.forEach((email) => {
            extracted.push(email);
            byEmail[email] = row;
          });
        }
      });
      if (!extracted.length) {
        throw new Error('No emails found in the uploaded file.');
      }
      setComposeRecipientRows({ headers, rows: mappedRows, byEmail });
      setCompose((prev) => ({
        ...prev,
        recipients: mergeRecipients(prev.recipients, extracted),
      }));
    } catch (err) {
      setUploadError(err.message || 'Failed to read file');
    } finally {
      event.target.value = '';
    }
  };

  const handleSend = async () => {
    setSendError('');
    try {
      const composeText = htmlToText(compose.body || '');
      await sendEmail({
        subject: compose.subject,
        body: composeText,
        bodyText: composeText,
        bodyHtml: compose.body || '',
        recipients: compose.recipients,
        accountId: compose.accountId,
        perAccountDaily: campaignPlan.perAccountDaily,
        perAccountLimits: campaignPlan.perAccountLimits || {},
        mergeData: composeRecipientRows.byEmail || {},
      });
      setCompose((prev) => ({
        ...prev,
        subject: '',
        body: '',
        recipients: [],
        manualBlock: '',
      }));
      setNotice({ open: true, type: 'success', message: 'Email sent successfully.', scope: 'compose' });
      if (isAuthenticated) {
        adminApi.logActivity({ type: 'email_send', source: 'compose' }, token).catch(() => {});
      }
    } catch (err) {
      setSendError(err.message || 'Unable to send email');
      setNotice({ open: true, type: 'error', message: err.message || 'Unable to send email', scope: 'compose' });
    }
  };

  const handleGroupSave = async () => {
    const name = (groupForm.name || '').trim();
    if (!name) {
      setNotice({ open: true, type: 'error', message: 'Group name is required.', scope: 'accounts' });
      return;
    }
    const payload = {
      id: groupForm.id || `grp-${Date.now()}`,
      name,
      accountIds: Array.from(new Set(groupForm.accountIds || [])),
      createdAt: groupForm.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setAccountGroups((prev) => {
      const exists = prev.findIndex((g) => g.id === payload.id);
      if (exists >= 0) {
        const next = [...prev];
        next[exists] = payload;
        return next;
      }
      return [payload, ...prev];
    });
    setGroupModalOpen(false);
    setGroupForm({ id: '', name: '', accountIds: [] });
    
    setNotice({ open: true, type: 'success', message: 'Group saved.', scope: 'accounts' });
  };

  const handleGroupEdit = (group) => {
    setGroupForm({
      id: group.id,
      name: group.name,
      accountIds: group.accountIds || [],
    });
    setGroupModalOpen(true);
  };

  const handleGroupDelete = async (id) => {
    setAccountGroups((prev) => prev.filter((g) => g.id !== id));
    setCampaignPlan((p) => ({
      ...p,
      selectedAccounts: (p.selectedAccounts || []).filter((key) => key !== `group:${id}`),
    }));
    
    setNotice({ open: true, type: 'info', message: 'Group removed.', scope: 'accounts' });
  };

  const handleReply = async (messageId) => {
    const replyHtml = replyDrafts[messageId] || '';
    const reply = htmlToText(replyHtml);
    if (!reply.trim()) {
      setSendError('Reply cannot be empty.');
      setNotice({ open: true, type: 'error', message: 'Reply cannot be empty.', scope: 'inbox' });
      return;
    }
    const msg = inbox.find((m) => m.id === messageId);
    const to = msg?.from || '';
    if (!to) {
      setSendError('No recipient found for this message.');
      setNotice({ open: true, type: 'error', message: 'No recipient found for this message.' });
      return;
    }
    try {
      setReplySending(true);
      setNotice({ open: true, type: 'info', message: 'Sending reply...', scope: 'inbox' });
        await sendEmail({
          subject: `Re: ${msg?.subject || ''}`,
          body: reply,
          bodyText: reply,
          bodyHtml: '',
          recipients: [to],
          accountId: compose.accountId,
          replyToId: messageId,
          attachments: [],
        });
      setReplyDrafts((prev) => ({ ...prev, [messageId]: '' }));
      setReplyAttachments([]);
      setMessageStatus(messageId, 'read');
      setNotice({ open: true, type: 'success', message: 'Reply sent.', scope: 'inbox' });
      if (isAuthenticated) {
        adminApi.logActivity({ type: 'email_reply', source: 'inbox', metadata: { messageId } }, token).catch(() => {});
      }
    } catch (err) {
      setSendError(err.message || 'Reply failed');
      setNotice({ open: true, type: 'error', message: err.message || 'Reply failed', scope: 'inbox' });
    } finally {
      setReplySending(false);
    }
  };

  const filteredInbox = useMemo(() => {
    let rows = [...inbox];
    if (filterUnread) {
      rows = rows.filter((m) => m.status === 'unread');
    }
    if (selectedAccountId) {
      rows = rows.filter(
        (m) => !m.accountId || m.accountId === selectedAccountId
      );
    }
    const query = String(inboxSearch || '').trim().toLowerCase();
    if (query) {
      rows = rows.filter((m) => {
        const text = `${m.from || ''} ${m.subject || ''} ${htmlToText(m.body || '')}`.toLowerCase();
        return text.includes(query);
      });
    }
    return rows;
  }, [inbox, filterUnread, selectedAccountId, inboxSearch]);

  const expandAccountSelection = useCallback(
    (selection = []) => {
      const selected = Array.isArray(selection) ? selection : [];
      if (!selected.length) return accounts.map((a) => a.id);
      const ids = new Set();
      selected.forEach((id) => {
        if (String(id || '').startsWith('group:')) {
          const gid = String(id).replace('group:', '');
          const grp = accountGroups.find((g) => g.id === gid);
          grp?.accountIds?.forEach((aid) => ids.add(aid));
        } else {
          ids.add(id);
        }
      });
      return Array.from(ids);
    },
    [accounts, accountGroups]
  );

  const accountProviders = useMemo(() => {
    const set = new Set();
    accounts.forEach((a) => {
      if (a.provider) set.add(a.provider);
    });
    return Array.from(set);
  }, [accounts]);

  const accountUsageToday = useMemo(() => {
    const map = {};
    const today = new Date().toISOString().slice(0, 10);
    sent.forEach((s) => {
      const accId = s.accountId || s.account_id;
      if (!accId) return;
      const ts = s.timestamp || s.sentAt || s.createdAt;
      if (!ts) return;
      if (new Date(ts).toISOString().slice(0, 10) !== today) return;
      map[accId] = (map[accId] || 0) + 1;
    });
    return map;
  }, [sent]);

  const visibleAccounts = useMemo(() => {
    const term = accountSearch.trim().toLowerCase();
    return accounts.filter((acc) => {
      if (accountProviderFilter && acc.provider !== accountProviderFilter) return false;
      if (!term) return true;
      const hay = `${acc.label || ''} ${acc.fromEmail || ''} ${acc.username || ''} ${acc.host || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [accounts, accountProviderFilter, accountSearch]);

  const selectedAccount =
    accounts.find((a) => a.id === (compose.accountId || selectedAccountId)) ||
    accounts[0];
  return (
    <>
    <Box sx={{ maxWidth: 1400, mx: 'auto', pb: 4, px: { xs: 1.5, sm: 2.5 } }}>
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 30%, #0ea5e9 100%)',
          color: '#e2e8f0',
        }}
      >
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Mail />
                <Typography variant="h4" fontWeight={800} color="#fff">
                  Email Studio
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Add SMTP/OAuth accounts, run campaigns, and reply from a unified inbox.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                icon={<Inbox fontSize="small" />}
                label={`${stats.unread} unread`}
                sx={{ backgroundColor: '#0ea5e9', color: '#0b1120', fontWeight: 700 }}
              />
              <Chip
                icon={<CloudDone fontSize="small" />}
                label={`${stats.totalSent} sent`}
                variant="outlined"
                sx={{ borderColor: '#e2e8f0', color: '#e2e8f0' }}
              />
              <Chip
                icon={<Visibility fontSize="small" />}
                label={`${trackingTotals.totalOpens || 0} opened`}
                variant="outlined"
                sx={{ borderColor: '#e2e8f0', color: '#e2e8f0' }}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v);
            // Clear campaign state when leaving the Campaign Planner tab
            if (v !== 2) {
              setComposePlanId('');
              setCampaignRun({ id: null, counts: {}, running: false });
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 2,
            pt: 1,
          }}
        >
          <Tab icon={<Settings />} iconPosition="start" label="Accounts" />
          <Tab icon={<Send />} iconPosition="start" label="Compose" />
          <Tab icon={<Campaign />} iconPosition="start" label="Campaign Planner" />
          <Tab icon={<Inbox />} iconPosition="start" label="Unified Inbox" />
          <Tab icon={<Visibility />} iconPosition="start" label="Seen Emails" />
          <Tab icon={<Layers />} iconPosition="start" label="Templates" />
        </Tabs>

        <CardContent>
          {activeTab === 0 && (
            <>
              {prefsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {prefsError}. Check login/session and `/api/user_accounts.php`.
                </Alert>
              )}
              <AccountsManager
                accounts={accounts}
                accountGroups={accountGroups}
                onAddAccount={() => {
                  setAccountForm(defaultAccount);
                  setEditingId('');
                  setAccountError('');
                  setAccountMessage('');
                  setAccountModalOpen(true);
                }}
                onEditAccount={handleAccountEdit}
                onDeleteAccount={handleAccountDelete}
                onTestAccount={handleInlineAccountTest}
                onCreateGroup={(accountIds) => {
                  setGroupForm({ id: '', name: '', accountIds: accountIds || [] });
                  setGroupModalOpen(true);
                }}
                onEditGroup={handleGroupEdit}
                onDeleteGroup={handleGroupDelete}
                selectedAccountId={selectedAccountId}
                onSelectAccount={setSelectedAccountId}
                bulkTesting={bulkTesting}
                onBulkTest={handleBulkAccountTest}
                accountUsageToday={accountUsageToday}
                campaignPlan={campaignPlan}
                compactView={compactAccounts}
                onCompactViewChange={setCompactAccounts}
              />
            </>
          )}
          {activeTab === 0 && false && (
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5} flexWrap="wrap">
                    <Box>
                      <Typography variant="h6" fontWeight={700}>Accounts & Groups</Typography>
                      <Typography variant="body2" color="text.secondary">Organize SMTP/OAuth accounts into reusable groups for campaigns.</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button size="small" variant="outlined" startIcon={<Groups />} onClick={() => { setGroupForm({ id: '', name: '', accountIds: [] }); setGroupModalOpen(true); }}>New Group</Button>
                      <Button size="small" variant="contained" startIcon={<Add />} onClick={() => { setAccountForm(defaultAccount); setEditingId(''); setAccountError(''); setAccountMessage(''); setTestPassed(false); setAccountModalOpen(true); }}>Add SMTP</Button>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <TextField size="small" placeholder="Search accounts" value={accountSearch} onChange={(e) => setAccountSearch(e.target.value)} sx={{ minWidth: 200, flex: 1 }} />
                      <TextField select size="small" label="Provider" value={accountProviderFilter} onChange={(e) => setAccountProviderFilter(e.target.value)} sx={{ minWidth: 160 }}>
                        <MenuItem value="">All providers</MenuItem>
                        {accountProviders.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
                      </TextField>
                      <FormControlLabel control={<Switch checked={compactAccounts} onChange={(e) => setCompactAccounts(e.target.checked)} size="small" />} label="Compact view" />
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip size="small" label={`Accounts: ${accounts.length}`} />
                      <Chip size="small" label={`Providers: ${accountProviders.length}`} variant="outlined" />
                      <Chip size="small" label={`Visible: ${visibleAccounts.length}`} variant="outlined" />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MailOutline />}
                        disabled={bulkTesting || visibleAccounts.length === 0}
                        onClick={handleBulkAccountTest}
                      >
                        {bulkTesting ? 'Testing...' : 'Test visible'}
                      </Button>
                      {(bulkTestSummary.success > 0 || bulkTestSummary.failed > 0) && (
                        <Chip
                          size="small"
                          color={bulkTestSummary.failed ? 'warning' : 'success'}
                          label={`Tested: ${bulkTestSummary.success} ok, ${bulkTestSummary.failed} failed`}
                        />
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccountTree fontSize="small" />
                        <Typography fontWeight={700}>Account Groups</Typography>
                      </Stack>
                      <Button size="small" variant="text" startIcon={<Add />} onClick={() => { setGroupForm({ id: '', name: '', accountIds: [] }); setGroupModalOpen(true); }}>Create</Button>
                    </Stack>
                    {accountGroups.length === 0 && (<Alert severity="info">No groups yet. Create one to rotate multiple accounts in campaigns.</Alert>)}
                    <Stack spacing={1}>
                      {accountGroups.map((grp) => {
                        const open = groupOpen[grp.id];
                        return (
                          <Card key={grp.id} variant="outlined">
                            <CardContent sx={{ py: 1.25 }}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <IconButton size="small" onClick={() => setGroupOpen((prev) => ({ ...prev, [grp.id]: !open }))}>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
                                  <Box>
                                    <Typography fontWeight={700}>{grp.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{grp.accountIds?.length || 0} accounts</Typography>
                                  </Box>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <Button size="small" variant="outlined" onClick={() => handleGroupEdit(grp)}>Edit</Button>
                                  <Button size="small" color="error" variant="text" onClick={() => handleGroupDelete(grp.id)}>Delete</Button>
                                </Stack>
                              </Stack>
                              <Collapse in={open} timeout="auto" unmountOnExit>
                                <Stack spacing={0.5} sx={{ mt: 1, pl: 5 }}>
                                  {(grp.accountIds || []).map((id) => {
                                    const acct = accounts.find((a) => a.id === id);
                                    if (!acct) return null;
                                    const accLimit = acct.dailyLimit || campaignPlan.perAccountDaily || 0;
                                    const used = accountUsageToday[acct.id] || 0;
                                    const remaining = accLimit > 0 ? Math.max(accLimit - used, 0) : '8';
                                    return (
                                      <Stack key={id} direction="row" spacing={1} alignItems="center">
                                        <CheckCircle fontSize="small" color={selectedAccountId === id ? 'success' : 'disabled'} />
                                        <Typography variant="body2" fontWeight={600}>{acct.label || acct.fromEmail || acct.username}</Typography>
                                        <Typography variant="caption" color="text.secondary">{acct.provider || 'SMTP'}</Typography>
                                        <Typography variant="caption" color="text.secondary">Limit: {accLimit > 0 ? accLimit : '8'}</Typography>
                                        <Typography variant="caption" color="text.secondary">Today: {used} • Rem: {remaining}</Typography>
                                      </Stack>
                                    );
                                  })}
                                  {(grp.accountIds || []).length === 0 && (<Typography variant="body2" color="text.secondary">No accounts assigned.</Typography>)}
                                </Stack>
                              </Collapse>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700}>Connected Accounts</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" label={`Default/day: ${campaignPlan.perAccountDaily || 0}`} variant="outlined" />
                      <Typography variant="body2" color="text.secondary">Showing {visibleAccounts.length} of {accounts.length}</Typography>
                    </Stack>
                  </Stack>
                  {accounts.length === 0 && (<Alert severity="warning" sx={{ mt: 1 }}>No accounts yet. Add SMTP or provider credentials to start sending.</Alert>)}
                  {accounts.length > 0 && visibleAccounts.length === 0 && (<Alert severity="info" sx={{ mt: 1 }}>No accounts match this filter.</Alert>)}
                  <Box sx={{ mt: 1, maxHeight: { xs: 360, sm: 520 }, overflow: 'auto' }}>
                    <Stack spacing={compactAccounts ? 0.75 : 1.25}>
                      {visibleAccounts.map((acc) => (
                        <Card key={acc.id} variant="outlined" sx={{ borderColor: selectedAccountId === acc.id ? 'primary.main' : 'divider', backgroundColor: selectedAccountId === acc.id ? 'rgba(59,130,246,0.06)' : 'inherit' }}>
                          <CardContent sx={{ py: compactAccounts ? 1 : 1.5, px: compactAccounts ? 1.25 : 2 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={compactAccounts ? 0.5 : 1}>
                              <Box>
                                <Typography fontWeight={700} noWrap>{acc.label || acc.fromEmail || acc.username}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>{acc.host}:{acc.port} {acc.secure ? '(TLS)' : ''}</Typography>
                              </Box>
                              <Stack direction="row" spacing={compactAccounts ? 0.5 : 1} alignItems="center" flexWrap="wrap">
                                <Chip icon={<Layers fontSize="small" />} label={acc.provider || 'SMTP'} size="small" variant="outlined" />
                                <Tooltip title="Set active">
                                  <IconButton size="small" onClick={() => setSelectedAccountId(acc.id)}>
                                    <CheckCircle color={selectedAccountId === acc.id ? 'success' : 'disabled'} fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Test connection">
                                  <IconButton size="small" onClick={() => handleInlineAccountTest(acc)}>
                                    <MailOutline fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => handleAccountEdit(acc)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" onClick={() => handleAccountDelete(acc.id)} color="error">
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Stack>
          )}
          {false && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                    >
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          Accounts & Groups
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Organize SMTP/OAuth accounts into reusable groups for campaigns.
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Groups />}
                          onClick={() => {
                            setGroupForm({ id: '', name: '', accountIds: [] });
                            setGroupModalOpen(true);
                          }}
                        >
                          New Group
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => {
                            setAccountForm(defaultAccount);
                            setEditingId('');
                            setAccountError('');
                            setAccountMessage('');
                            setAccountModalOpen(true);
                          }}
                        >
                          Add SMTP
                        </Button>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <TextField
                          size="small"
                          placeholder="Search accounts"
                          value={accountSearch}
                          onChange={(e) => setAccountSearch(e.target.value)}
                          sx={{ minWidth: 200 }}
                        />
                        
                        <FormControlLabel
                          control={
                            <Switch
                              checked={compactAccounts}
                              onChange={(e) => setCompactAccounts(e.target.checked)}
                              size="small"
                            />
                          }
                          label="Compact view"
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5} lg={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Account Groups
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<Add />}
                        onClick={() => {
                          setGroupForm({ id: '', name: '', accountIds: [] });
                          setGroupModalOpen(true);
                        }}
                      >
                        Create
                      </Button>
                    </Stack>
                    {accountGroups.length === 0 && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        No groups yet. Create one to rotate multiple accounts in campaigns.
                      </Alert>
                    )}
                    <Stack spacing={1}>
                      {accountGroups.map((grp) => (
                        <Card key={grp.id} variant="outlined">
                          <CardContent sx={{ py: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                              <Box>
                                <Typography fontWeight={700}>{grp.name}</Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5} flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    label={`${grp.accountIds?.length || 0} account(s)`}
                                    variant="outlined"
                                  />
                                  {(grp.accountIds || []).slice(0, 3).map((id) => {
                                    const acct = accounts.find((a) => a.id === id);
                                    return (
                                      <Chip
                                        key={id}
                                        size="small"
                                        label={acct?.label || acct?.fromEmail || id}
                                        sx={{ maxWidth: 140 }}
                                      />
                                    );
                                  })}
                                  {(grp.accountIds || []).length > 3 && (
                                    <Typography variant="caption" color="text.secondary">
                                      +{(grp.accountIds || []).length - 3} more
                                    </Typography>
                                  )}
                                </Stack>
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Button size="small" variant="outlined" onClick={() => handleGroupEdit(grp)}>
                                  Edit
                                </Button>
                                <Button size="small" color="error" variant="text" onClick={() => handleGroupDelete(grp.id)}>
                                  Delete
                                </Button>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={7} lg={8}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Connected Accounts
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Showing {visibleAccounts.length} of {accounts.length}
                      </Typography>
                    </Stack>
                    {accounts.length === 0 && (
                      <Alert severity="warning">
                        No accounts yet. Add SMTP or provider credentials to start sending.
                      </Alert>
                    )}
                    {accounts.length > 0 && visibleAccounts.length === 0 && (
                      <Alert severity="info">No accounts match this filter.</Alert>
                    )}
                    <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
                      <Stack spacing={compactAccounts ? 0.75 : 1.25}>
                        {visibleAccounts.map((acc) => (
                        <Card
                          key={acc.id}
                          variant="outlined"
                          sx={{
                            borderColor:
                                selectedAccountId === acc.id ? 'primary.main' : 'divider',
                              backgroundColor:
                                selectedAccountId === acc.id ? 'rgba(59,130,246,0.06)' : 'inherit',
                            }}
                          >
                            <CardContent sx={{ py: compactAccounts ? 1 : 1.5, px: compactAccounts ? 1.25 : 2 }}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                spacing={compactAccounts ? 0.5 : 1}
                              >
                                <Box>
                                  <Typography fontWeight={700} noWrap>
                                    {acc.label || acc.fromEmail || acc.username}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {acc.host}:{acc.port} {acc.secure ? '(TLS)' : ''}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                                    <Chip
                                      icon={<Layers fontSize="small" />}
                                      label={acc.provider || 'SMTP'}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Limit: ${acc.dailyLimit || campaignPlan.perAccountDaily || 0}`}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Sent today: ${accountUsageToday[acc.id] || 0}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={`Remaining: ${
                                        (acc.dailyLimit || campaignPlan.perAccountDaily || 0) > 0
                                          ? Math.max(
                                              (acc.dailyLimit || campaignPlan.perAccountDaily || 0) -
                                                (accountUsageToday[acc.id] || 0),
                                              0
                                            )
                                          : '8'
                                      }`}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  </Stack>
                                </Box>
                                <Stack direction="row" spacing={compactAccounts ? 0.5 : 1} alignItems="center" flexWrap="wrap">
                                  <Tooltip title="Set active">
                                    <IconButton
                                      size="small"
                                      onClick={() => setSelectedAccountId(acc.id)}
                                    >
                                      <CheckCircle
                                        color={
                                          selectedAccountId === acc.id ? 'success' : 'disabled'
                                        }
                                        fontSize="small"
                                      />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Test connection">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleInlineAccountTest(acc)}
                                    >
                                      <MailOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleAccountEdit(acc)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleAccountDelete(acc.id)}
                                    color="error"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                Usage today: {accountUsageToday[acc.id] || 0} /{' '}
                                {acc.dailyLimit || campaignPlan.perAccountDaily || '8'} • Remaining:{' '}
                                {(acc.dailyLimit || campaignPlan.perAccountDaily || 0) > 0
                                  ? Math.max(
                                      (acc.dailyLimit || campaignPlan.perAccountDaily || 0) -
                                        (accountUsageToday[acc.id] || 0),
                                      0
                                    )
                                  : '8'}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

            </Grid>
          )}
          {activeTab === 1 && (
            <Box>
              {sendError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {sendError}
                </Alert>
              )}
              {statusText && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {statusText}
                </Alert>
              )}
              {sendProgress && sendProgress.total > 1 && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={700}>
                        Sending {sendProgress.total} emails…
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.round((sendProgress.current / sendProgress.total) * 100)}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Sent {sendProgress.sent} • Failed {sendProgress.failed} • Remaining {Math.max(sendProgress.total - sendProgress.current, 0)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              )}
              <AdvancedEmailComposer
                compose={compose}
                setCompose={setCompose}
                accounts={accounts}
                carrierEmails={carrierEmails}
                recipientStats={recipientStats}
                subjectInsights={subjectInsights}
                bodyInsights={bodyInsights}
                fileInputRef={fileInputRef}
                onFileImport={handleFileImport}
                uploadError={uploadError}
                mergeTags={mergeTagOptions}
                onSend={handleSend}
                sendLoading={sending}
              />
              {sendProgress && sendProgress.total > 1 && (
                <Card
                  variant="outlined"
                  sx={{
                    mt: 2,
                    position: 'sticky',
                    bottom: 8,
                    zIndex: 2,
                    backgroundColor: 'background.paper',
                    borderColor: 'divider',
                  }}
                >
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={700}>
                          Sending emails
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.round((sendProgress.current / sendProgress.total) * 100)}
                        />
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" label={`Total ${sendProgress.total}`} />
                        <Chip size="small" color="success" label={`Sent ${sendProgress.sent}`} />
                        <Chip size="small" color="error" label={`Failed ${sendProgress.failed}`} />
                        <Chip size="small" variant="outlined" label={`Remaining ${Math.max(sendProgress.total - sendProgress.current, 0)}`} />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
          {activeTab === 2 && (
            <CampaignPlanner
              plans={savedPlans.map((p) => ({
                ...p,
                status: p.status || 'draft',
                scheduledDate: p.scheduledDate || null,
                timezone: p.timezone || 'UTC',
                segmentType: p.segmentType || 'all',
              }))}
              campaigns={savedPlans}
              accounts={accounts}
              accountGroups={accountGroups}
              templates={campaignTemplates}
              onRecipientColumns={(cols) => setPlannerRecipientColumns(cols || [])}
              onCreatePlan={(payload) => {
                const name = (payload?.name || '').trim();
                if (!name) return;
                const selectedTemplates = payload.selectedTemplates || [];
                const selectedAccounts = payload.selectedAccounts || [];
                const newPlan = {
                  id: `plan-${Date.now()}`,
                  name,
                  description: payload.description || '',
                  status: payload.scheduledDate ? 'scheduled' : 'draft',
                  scheduledDate: payload.scheduledDate || null,
                  timezone: payload.timezone || 'UTC',
                  segmentType: payload.segmentType || 'all',
                  sendingFrequency: payload.sendingFrequency || 'immediate',
                  recipientLimit: payload.recipientLimit || '',
                  recipientFileName: payload.recipientFileName || '',
                  recipientRows: payload.recipientRows || [],
                  templates: campaignTemplates.filter((t) => selectedTemplates.includes(t.id)),
                    strategy: {
                      ...defaultCampaignPlan,
                      selectedTemplates,
                      selectedAccounts,
                      perAccountLimits: {},
                      minDelaySec: Number(payload.minDelaySec) || defaultCampaignPlan.minDelaySec,
                      maxDelaySec: Number(payload.maxDelaySec) || defaultCampaignPlan.maxDelaySec,
                      rotateAccounts: payload.rotateAccounts !== false,
                      shuffleTemplates: payload.shuffleTemplates !== false,
                      jitterSec: Number(payload.jitterSec) || 0,
                      batchPauseEvery: Number(payload.batchPauseEvery) || 0,
                      batchPauseSec: Number(payload.batchPauseSec) || 0,
                      maxPerAccountPerHour: Number(payload.maxPerAccountPerHour) || 0,
                      maxFailures: Number(payload.maxFailures) || 0,
                      enforceMinDelay: payload.enforceMinDelay !== false,
                      trackOpens: payload.trackOpens !== false,
                      trackClicks: payload.trackClicks !== false,
                    },
                };
                const record = normalizePlanRecord(newPlan);
                upsertPlanRecord(record);
                savePlanToDatabase(record)
                  .then((saved) => {
                    if (saved) upsertPlanRecord(saved);
                  })
                  .catch((error) => {
                    console.error('Error saving plan:', error);
                    setNotice({ open: true, type: 'error', message: 'Failed to save plan to database.', scope: 'campaign' });
                  });
              }}
              onEditPlan={(plan) => handlePlanEdit(plan)}
              onDeletePlan={(id) => {
                setSavedPlans((prev) => prev.filter((p) => p.id !== id));
                deletePlanFromDatabase(id).catch((error) => {
                  console.error('Error deleting plan:', error);
                  setNotice({ open: true, type: 'error', message: 'Failed to delete plan from database.', scope: 'campaign' });
                });
              }}
              onSchedulePlan={() => {}}
              onApplySegmentation={() => {}}
              onLaunchCampaign={(id) => launchPlannerCampaign(id)}
              planStats={plannerStats}
            />
          )}
          {activeTab === 3 && (
            <InboxManager
              emails={inboxEmails || []}
              threads={threads || []}
              unreadCount={unreadCount}
              onReplyEmail={() => {}}
              onDeleteEmail={() => {}}
              onArchiveEmail={() => {}}
              onMarkAsRead={() => {}}
              onFlagEmail={() => {}}
              onSearchEmails={() => {}}
              selectedEmailId={selectedEmailId}
              onSelectEmail={setSelectedEmailId}
            />
          )}
          {activeTab === 4 && (
            <TrackingAnalytics
              trackingData={trackingData || []}
              emailStats={emailStats || {}}
              campaignAnalytics={campaignAnalytics || {}}
              onExportReport={() => {}}
              onShareStats={() => {}}
            />
          )}
          {activeTab === 5 && (
            <TemplateManager
              templates={campaignTemplates}
              mergeTags={mergeTagOptions}
              onCreateTemplate={saveTemplate}
              onEditTemplate={saveTemplate}
              onDeleteTemplate={deleteTemplate}
              onDuplicateTemplate={() => {}}
              onUseTemplate={(template) => {
                applyTemplateToCompose(template);
                setActiveTab(1);
              }}
              stats={{
                totalTemplates: campaignTemplates.length,
                avgOpenRate: campaignTemplates.length > 0 
                  ? Math.round((campaignTemplates.reduce((sum, t) => sum + (parseFloat(t.openRate) || 0), 0) / campaignTemplates.length) * 100) / 100 
                  : 0,
                totalUses: campaignTemplates.reduce((sum, t) => sum + (t.uses || 0), 0),
                favorites: campaignTemplates.filter(t => t.isFavorite).length
              }}
            />
          )}
          {activeTab === 2 && false && (
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Layers fontSize="small" />
                      <Typography fontWeight={700}>Template Library</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                      <TextField
                        size="small"
                        placeholder="Search templates"
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        sx={{ minWidth: 200 }}
                      />
                      <Button size="small" variant="contained" startIcon={<Add />} onClick={() => { setTemplateForm({ id: '', name: '', subject: '', body: '' }); setTemplateModalOpen(true); }}>New Template</Button>
                      <Chip size="small" label={`${filteredTemplates.length} of ${campaignTemplates.length}`} />
                      <Chip size="small" color="info" variant="outlined" label={`In strategy: ${campaignPlan.selectedTemplates.length}`} />
                    </Stack>
                  </Stack>
                  {campaignTemplates.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No templates yet. Add one to reuse in Compose and Planner.
                    </Typography>
                  )}
                  <Stack spacing={1.2} sx={{ maxHeight: 420, overflowY: 'auto', mt: 1 }}>
                    {filteredTemplates.map((t) => {
                      const inStrategy = campaignPlan.selectedTemplates.includes(t.id);
                      return (
                        <Card key={t.id} variant="outlined" sx={{ p: 1 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={700} noWrap>{t.name}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>{t.subject}</Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {inStrategy && <Chip size="small" color="success" label="In strategy" />}
                              <Button size="small" variant="outlined" onClick={() => applyTemplateToCompose(t)}>Use</Button>
                              <Button size="small" variant="text" onClick={() => { setTemplateForm(t); setTemplateModalOpen(true); }}>Edit</Button>
                              <Button size="small" variant="text" onClick={() => setCampaignPlan((p) => ({ ...p, selectedTemplates: inStrategy ? p.selectedTemplates.filter((id) => id !== t.id) : Array.from(new Set([...(p.selectedTemplates || []), t.id])) }))}>
                                {inStrategy ? 'Remove' : 'Add'}
                              </Button>
                              <Button size="small" color="error" variant="text" onClick={() => deleteTemplate(t.id)}>Delete</Button>
                            </Stack>
                          </Stack>
                        </Card>
                      );
                    })}
                    {campaignTemplates.length > 0 && filteredTemplates.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No templates match the current search.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" mb={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Campaign fontSize="small" />
                      <Typography fontWeight={700}>Send Strategy</Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        const name = prompt('Save this plan as:');
                        if (name) savePlan(name);
                      }}
                    >
                      Save plan
                    </Button>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Store sending preferences per account or group. Strategy and plans save to your account and can be loaded in Compose.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Accounts & Groups</Typography>
                      </Stack>
                      <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        {accountGroups.length > 0 && (
                          <Box>
                            <Typography variant="caption" fontWeight={700} color="text.secondary">Groups</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                              {accountGroups.map((g) => {
                                const key = `group:${g.id}`;
                                const active = campaignPlan.selectedAccounts.includes(key);
                                return (
                                  <Chip
                                    key={key}
                                    label={`${g.name} (${g.accountIds?.length || 0})`}
                                    color={active ? 'primary' : 'default'}
                                    onClick={() =>
                                      setCampaignPlan((p) => ({
                                        ...p,
                                        selectedAccounts: active
                                          ? p.selectedAccounts.filter((id) => id !== key)
                                          : [...p.selectedAccounts, key],
                                      }))
                                    }
                                  />
                                );
                              })}
                            </Stack>
                          </Box>
                        )}
                        {accounts.map((a) => {
                          const active = campaignPlan.selectedAccounts.includes(a.id);
                          const perLimit = (campaignPlan.perAccountLimits || {})[a.id] || '';
                          return (
                            <Card key={a.id} variant="outlined" sx={{ p: 0.75 }}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                                <Chip
                                  label={a.label || a.fromEmail || a.username}
                                  color={active ? 'primary' : 'default'}
                                  onClick={() =>
                                    setCampaignPlan((p) => ({
                                      ...p,
                                      selectedAccounts: active
                                        ? p.selectedAccounts.filter((id) => id !== a.id)
                                        : [...p.selectedAccounts, a.id],
                                    }))
                                  }
                                />
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Emails to send"
                                    value={perLimit}
                                    placeholder="0"
                                    onChange={(e) =>
                                      setCampaignPlan((p) => ({
                                        ...p,
                                        perAccountLimits: {
                                          ...(p.perAccountLimits || {}),
                                          [a.id]: Number(e.target.value) || 0,
                                        },
                                      }))
                                    }
                                    sx={{ width: 120 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Uses group rotation/shuffle when groups selected.
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Card>
                          );
                        })}
                        {accounts.length === 0 && (<Typography variant="body2" color="text.secondary">Add an account first.</Typography>)}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Cadence & Randomization</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}><TextField label="Per account / day" type="number" size="small" value={campaignPlan.perAccountDaily} onChange={(e) => setCampaignPlan((p) => ({ ...p, perAccountDaily: Number(e.target.value) || 0 }))} fullWidth /></Grid>
                        <Grid item xs={6}><TextField label="Batch size" type="number" size="small" value={campaignPlan.batchSize} onChange={(e) => setCampaignPlan((p) => ({ ...p, batchSize: Number(e.target.value) || 0 }))} fullWidth /></Grid>
                        <Grid item xs={6}><TextField label="Min delay (s)" type="number" size="small" value={campaignPlan.minDelaySec} onChange={(e) => setCampaignPlan((p) => ({ ...p, minDelaySec: Number(e.target.value) || 0 }))} fullWidth /></Grid>
                        <Grid item xs={6}><TextField label="Max delay (s)" type="number" size="small" value={campaignPlan.maxDelaySec} onChange={(e) => setCampaignPlan((p) => ({ ...p, maxDelaySec: Number(e.target.value) || 0 }))} fullWidth /></Grid>
                        <Grid item xs={12}><TextField label="Random start offset (s)" type="number" size="small" value={campaignPlan.startOffsetSec || 0} onChange={(e) => setCampaignPlan((p) => ({ ...p, startOffsetSec: Number(e.target.value) || 0 }))} fullWidth helperText="Add a random delay before first send to avoid patterns." /></Grid>
                      </Grid>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} mt={1} flexWrap="wrap">
                        <FormControlLabel control={<Switch checked={campaignPlan.shuffleTemplates} onChange={(e) => setCampaignPlan((p) => ({ ...p, shuffleTemplates: e.target.checked }))} />} label="Shuffle templates" />
                        <FormControlLabel control={<Switch checked={campaignPlan.rotateAccounts} onChange={(e) => setCampaignPlan((p) => ({ ...p, rotateAccounts: e.target.checked }))} />} label="Rotate accounts" />
                        <FormControlLabel control={<Switch checked onChange={() => {}} />} label="Group-aware rotation" />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Strategy and saved plans are stored in your account. Rotation and shuffle help spread risk across accounts and content.
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} mb={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Campaign fontSize="small" />
                      <Typography fontWeight={700}>Saved Plans</Typography>
                    </Stack>
                    <Chip size="small" label={`${savedPlans.length} saved`} />
                  </Stack>
                  {savedPlans.length === 0 && (<Typography variant="body2" color="text.secondary">No saved plans.</Typography>)}
                  {savedPlans.length > 0 && (
                    <Card variant="outlined">
                      <TableContainer sx={{ maxHeight: 420 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Plan</TableCell>
                              <TableCell>Templates</TableCell>
                              <TableCell>Accounts</TableCell>
                              <TableCell>Cadence</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {savedPlans.map((p) => {
                              const open = planOpen[p.id];
                              const acctIds = expandAccountSelection(p.strategy.selectedAccounts);
                              const acctCount = acctIds.length || 'All';
                              return (
                                <React.Fragment key={p.id}>
                                  <TableRow hover>
                                    <TableCell sx={{ minWidth: 200 }}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => setPlanOpen((prev) => ({ ...prev, [p.id]: !open }))}
                                        >
                                          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                        </IconButton>
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography fontWeight={700} noWrap>{p.name}</Typography>
                                          <Typography variant="caption" color="text.secondary" noWrap>
                                            Rotation: {p.strategy.rotateAccounts ? 'On' : 'Off'} • Shuffle: {p.strategy.shuffleTemplates ? 'On' : 'Off'}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </TableCell>
                                    <TableCell>{(p.strategy.selectedTemplates || []).length}</TableCell>
                                    <TableCell>{acctCount}</TableCell>
                                    <TableCell>
                                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        <Chip size="small" label={`${p.strategy.perAccountDaily || 0}/day`} />
                                        <Chip size="small" label={`Batch ${p.strategy.batchSize || 0}`} />
                                        <Chip size="small" label={`${p.strategy.minDelaySec || 0}-${p.strategy.maxDelaySec || 0}s`} />
                                      </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Tooltip title="Load to Compose">
                                        <IconButton size="small" onClick={() => loadPlanToCompose(p)}><Send fontSize="small" /></IconButton>
                                      </Tooltip>
                                      <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => handlePlanEdit(p)}><Edit fontSize="small" /></IconButton>
                                      </Tooltip>
                                      <Tooltip title="Details">
                                        <IconButton
                                          size="small"
                                          onClick={() => setPlanOpen((prev) => ({ ...prev, [p.id]: !open }))}
                                        >
                                          <Visibility fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => {
                                            setSavedPlans((prev) => prev.filter((x) => x.id !== p.id));
                                            deletePlanFromDatabase(p.id).catch((error) => {
                                              console.error('Error deleting plan:', error);
                                              setNotice({ open: true, type: 'error', message: 'Failed to delete plan from database.', scope: 'campaign' });
                                            });
                                          }}
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell colSpan={5} sx={{ p: 0, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
                                      <Collapse in={open} timeout="auto" unmountOnExit>
                                        <Box sx={{ p: 2, backgroundColor: 'action.hover' }}>
                                          <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                              <Typography variant="subtitle2">Cadence</Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Per account/day: {p.strategy.perAccountDaily || 0} • Batch: {p.strategy.batchSize || 0}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary">
                                                Delays: {p.strategy.minDelaySec || 0}s - {p.strategy.maxDelaySec || 0}s • Start offset: {p.strategy.startOffsetSec || 0}s
                                              </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                              <Typography variant="subtitle2">Templates</Typography>
                                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                {(p.strategy.selectedTemplates || []).map((id) => {
                                                  const tpl = campaignTemplates.find((t) => t.id === id);
                                                  return <Chip key={id} size="small" label={tpl?.name || id} />;
                                                })}
                                                {(p.strategy.selectedTemplates || []).length === 0 && (
                                                  <Typography variant="body2" color="text.secondary">None</Typography>
                                                )}
                                              </Stack>
                                            </Grid>
                                            <Grid item xs={12}>
                                              <Typography variant="subtitle2">Accounts</Typography>
                                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                {acctIds.map((id) => {
                                                  const acct = accounts.find((a) => a.id === id);
                                                  return <Chip key={id} size="small" label={acct?.label || acct?.fromEmail || id} icon={<Mail fontSize="small" />} />;
                                                })}
                                                {acctIds.length === 0 && (
                                                  <Typography variant="body2" color="text.secondary">All accounts</Typography>
                                                )}
                                              </Stack>
                                            </Grid>
                                          </Grid>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  )}
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1} alignItems="flex-start">
                    <Typography variant="subtitle2" fontWeight={700}>How to use</Typography>
                    <Typography variant="body2" color="text.secondary">1) Choose accounts or groups and set per-account limits.</Typography>
                    <Typography variant="body2" color="text.secondary">2) Adjust cadence: batch size, delays, and optional random start.</Typography>
                    <Typography variant="body2" color="text.secondary">3) Save the plan to reuse later.</Typography>
                    <Typography variant="body2" color="text.secondary">4) In Compose, load the saved plan, pick a template, and send.</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          )}

          {activeTab === 3 && (
            <Grid container spacing={2} sx={{ height: { xs: 'auto', md: '82vh' } }}>
              <Grid item xs={12}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  mb={1}
                >
                  <Typography variant="h6" fontWeight={700}>
                    Unified Inbox
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={() => {
                        fetchInbox().catch(err => {
                          let errorMsg = err.message || 'Inbox refresh failed';
                          
                          // Provide helpful guidance for IMAP errors
                          if (errorMsg.includes('IMAP') || errorMsg.includes('imap')) {
                            errorMsg = errorMsg + ' | Check: http://localhost:8080/api/imap_check.php for diagnostics';
                          }
                          
                          setNotice({
                            open: true,
                            type: 'error',
                            message: errorMsg,
                            scope: 'inbox'
                          });
                        });
                      }}
                      disabled={fetchingInbox}
                    >
                      {fetchingInbox ? 'Refreshing...' : 'Refresh'}
                    </Button>
                    <TextField
                      size="small"
                      placeholder="Search sender or subject"
                      value={inboxSearch}
                      onChange={(e) => setInboxSearch(e.target.value)}
                      sx={{ minWidth: 200 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={filterUnread}
                          onChange={(e) => setFilterUnread(e.target.checked)}
                        />
                      }
                      label="Show unread only"
                    />
                    {inboxSearch && (
                      <Button size="small" variant="text" onClick={() => setInboxSearch('')}>
                        Clear search
                      </Button>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Toggle new email notifications">
                        <IconButton
                          onClick={() =>
                            setNotificationPrefs((p) => ({ ...p, newEmail: !p.newEmail }))
                          }
                          color={notificationPrefs.newEmail ? 'success' : 'default'}
                          size="small"
                        >
                          <MailOutline />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Toggle read email notifications">
                        <IconButton
                          onClick={() =>
                            setNotificationPrefs((p) => ({ ...p, seen: !p.seen }))
                          }
                          color={notificationPrefs.seen ? 'success' : 'default'}
                          size="small"
                        >
                          <MarkEmailRead />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Toggle notification sound">
                        <IconButton
                          onClick={() =>
                            setNotificationPrefs((p) => ({ ...p, sound: !p.sound }))
                          }
                          color={notificationPrefs.sound ? 'success' : 'default'}
                          size="small"
                        >
                          <VolumeUp />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Chip
                      icon={<WarningAmber fontSize="small" />}
                      label={`${filteredInbox.length} message${filteredInbox.length === 1 ? '' : 's'}`}
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <List
                  dense
                  sx={{
                    maxHeight: { xs: 320, md: '80vh' },
                    overflow: 'auto',
                    pr: 0.5,
                  }}
                >
                  {filteredInbox.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No messages"
                        secondary="New inbound emails will appear here once accounts are connected."
                      />
                    </ListItem>
                  )}
                  {filteredInbox.map((msg) => (
                    <Box
                      key={msg.id}
                      onClick={() => {
                        setActiveMessageId(msg.id);
                        setMessageStatus(msg.id, 'read');
                      }}
                      sx={{
                        border: '1px solid',
                        borderColor: activeMessageId === msg.id ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        mb: 1,
                        cursor: 'pointer',
                        backgroundColor: activeMessageId === msg.id ? 'action.hover' : 'background.paper',
                      }}
                    >
                      <ListItem alignItems="flex-start" sx={{ py: 0.75 }}>
                        <Avatar
                          sx={{ mr: 1.5, width: 32, height: 32, fontSize: 12 }}
                          src={resolveAvatar(msg) || undefined}
                        >
                          {msg.from?.slice(0, 2)?.toUpperCase() || 'IN'}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                              <Typography fontWeight={700} noWrap sx={{ maxWidth: '70%', fontSize: 13 }}>
                                {msg.from || '(Unknown sender)'}
                              </Typography>
                              <Chip
                                size="small"
                                label={msg.status === 'unread' ? 'Unread' : 'Read'}
                                color={msg.status === 'unread' ? 'primary' : 'default'}
                                variant="outlined"
                              />
                            </Stack>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(msg.timestamp).toLocaleString()}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.25,
                                  display: '-webkit-box',
                                  overflow: 'hidden',
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: 'vertical',
                                  fontSize: 12.5,
                                }}
                              >
                                {msg.subject || '(No subject)'}
                              </Typography>
                            </>
                          }
                        />
                     </ListItem>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, pb: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            msg.status === 'unread' ? <MarkEmailRead /> : <MarkEmailUnread />
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            setMessageStatus(msg.id, msg.status === 'unread' ? 'read' : 'unread');
                          }}
                        >
                          {msg.status === 'unread' ? 'Mark read' : 'Mark unread'}
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMessage(msg.id);
                            if (activeMessageId === msg.id) setActiveMessageId('');
                          }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12} md={8}>
                {(() => {
                  const active = inbox.find((m) => m.id === activeMessageId) || filteredInbox[0];
                  const thread = sent.filter((s) => s.parentId === (active?.id || ''));
                  if (!active) {
                    return (
                      <Card>
                        <CardContent>
                          <Typography>No message selected.</Typography>
                        </CardContent>
                      </Card>
                    );
                  }
                  return (
                    <Card sx={{ height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="h6" fontWeight={800}>{active.subject || '(No subject)'}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {active.from} • {new Date(active.timestamp).toLocaleString()}
                          </Typography>
                        </Stack>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Conversation
                          </Typography>
                          <Stack
                            spacing={1}
                            sx={{
                              flex: 1,
                              minHeight: 260,
                              maxHeight: { xs: 300, md: '45vh' },
                              overflowY: 'auto',
                              backgroundColor: 'rgba(0,0,0,0.03)',
                              borderRadius: 2,
                              p: 1.25,
                            }}
                          >
                            {(() => {
                              const normalizeSubject = (str = '') => {
                                let out = str.trim();
                                // strip any number of reply/forward prefixes so threads stick together
                                while (/^(re:|fw:|fwd:)\s*/i.test(out)) {
                                  out = out.replace(/^(re:|fw:|fwd:)\s*/i, '').trim();
                                }
                                return out.toLowerCase();
                              };
                              const subjectKey = normalizeSubject(active.subject || '');
                              const relatedInbox = inbox.filter((m) => normalizeSubject(m.subject || '') === subjectKey);
                              const relatedSent = sent.filter(
                                (s) =>
                                  s.parentId === active.id ||
                                  normalizeSubject(s.subject || '') === subjectKey
                              );
                              const combined = [
                                ...relatedInbox.map((m) => ({
                                  __type: 'in',
                                  id: m.id,
                                  from: m.from,
                                  body: m.body,
                                  ts: m.timestamp,
                                })),
                                ...relatedSent.map((s) => ({
                                  __type: 'out',
                                  id: s.id,
                                  from: 'You',
                                  body: s.body,
                                  ts: s.sentAt,
                                  messageId: s.messageId || s.id,
                                })),
                              ];
                              const repliesCount = Math.max(0, relatedInbox.length - 1);
                              const primarySent = relatedSent.find((s) => s.messageId || s.id);
                              const tracking = primarySent ? trackingMap[primarySent.messageId || primarySent.id] : null;

                              const seen = new Set();
                              const unique = combined.filter((m) => {
                                const key = `${m.__type}-${m.id}`;
                                if (seen.has(key)) return false;
                                seen.add(key);
                                return true;
                              });

                              return (
                                <>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                                    <Chip label={`Sent: ${relatedSent.length}`} size="small" />
                                    <Chip
                                      label={`Opens: ${tracking?.totalOpens ?? 0}`}
                                      size="small"
                                      color={tracking?.totalOpens >= 2 ? 'success' : 'default'}
                                      icon={<Visibility fontSize="small" />}
                                    />
                                    <Chip label={`Replies: ${repliesCount}`} size="small" />
                                    {tracking?.multiOpenRecipients?.length ? (
                                      <Chip
                                        label="Highly engaged"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                      />
                                    ) : null}
                                  </Stack>
                                  {unique
                                    .sort((a, b) => new Date(a.ts) - new Date(b.ts))
                                    .map((item) => {
                                      const isOut = item.__type === 'out';
                                      return (
                                        <Box
                                          key={`${item.__type}-${item.id}`}
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'flex-start',
                                            gap: 0.6,
                                            justifyContent: isOut ? 'flex-end' : 'flex-start',
                                            width: '100%',
                                          }}
                                        >
                                          {!isOut && (
                                            <Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>
                                              {item.from?.slice(0, 2)?.toUpperCase() || 'IN'}
                                            </Avatar>
                                          )}
                                          <Box
                                            sx={{
                                              maxWidth: '86%',
                                              px: 0.9,
                                              py: 0.6,
                                              borderRadius: isOut ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                                              backgroundColor: isOut ? 'primary.main' : 'background.paper',
                                              color: isOut ? 'primary.contrastText' : 'text.primary',
                                              boxShadow: 1,
                                              textAlign: isOut ? 'right' : 'left',
                                              wordBreak: 'break-word',
                                              ml: isOut ? 'auto' : 0,
                                            }}
                                          >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              whiteSpace: 'pre-wrap',
                                              lineHeight: 1.35,
                                              fontSize: 12.5,
                                              textAlign: isOut ? 'right' : 'left',
                                            }}
                                          >
                                            {cleanBody(item.body)}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              display: 'block',
                                              opacity: 0.75,
                                              mt: 0.1,
                                              fontSize: 10.5,
                                            }}
                                          >
                                            {new Date(item.ts || Date.now()).toLocaleString()}
                                          </Typography>
                                        </Box>
                                          {isOut && (
                                            <Avatar sx={{ width: 20, height: 20, fontSize: 10, ml: 0.5 }}>
                                              You
                                            </Avatar>
                                          )}
                                        </Box>
                                      );
                                    })}
                                </>
                              );
                            })()}
                          </Stack>
                        </Stack>

                        <Box
                          sx={{
                            position: 'sticky',
                            bottom: 0,
                            backgroundColor: 'background.paper',
                            py: 1,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            zIndex: 2,
                          }}
                        >
                          <TextField
                            multiline
                            minRows={2}
                            maxRows={3}
                            fullWidth
                            size="small"
                            placeholder="Type your reply..."
                            value={replyDrafts[active.id] || ''}
                            onChange={(e) =>
                              setReplyDrafts((prev) => ({
                                ...prev,
                                [active.id]: e.target.value,
                              }))
                            }
                          />
                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" mt={1}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={replySending ? null : <Reply />}
                              onClick={() => handleReply(active.id)}
                              disabled={!replyDrafts[active.id]?.trim() || replySending}
                            >
                              {replySending ? 'Sending...' : 'Reply'}
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Delete />}
                              onClick={() =>
                                setReplyDrafts((prev) => {
                                  const next = { ...prev };
                                  delete next[active.id];
                                  return next;
                                })
                              }
                            >
                              Clear
                            </Button>
                            <Tooltip title="Toggle raw view">
                              <IconButton onClick={() => setShowRaw((v) => !v)} size="small">
                                {showRaw ? <MarkEmailRead /> : <MarkEmailUnread />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })()}
              </Grid>
            </Grid>
          )}

          {activeTab === 4 && (
            <Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1}
                mb={2}
              >
                <Typography variant="h6" fontWeight={700}>
                  Seen Emails
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    icon={<Visibility fontSize="small" sx={{ color: 'green' }} />}
                    label={`${filteredEngagedGroups.length} engaged`}
                    variant="outlined"
                    sx={{ color: 'green', borderColor: 'green' }}
                  />
                  <TextField
                    size="small"
                    placeholder="Search recipients"
                    value={engagedSearch}
                    onChange={(e) => setEngagedSearch(e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Min opens"
                    value={engagedMinOpens}
                    onChange={(e) => setEngagedMinOpens(Number(e.target.value) || 0)}
                    sx={{ width: 120 }}
                  />
                  {(engagedSearch || engagedMinOpens) && (
                    <Button size="small" variant="text" onClick={() => { setEngagedSearch(''); setEngagedMinOpens(0); }}>
                      Clear filters
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() =>
                      refreshSeenStats().catch((err) =>
                        setNotice({
                          open: true,
                          type: 'error',
                          message: err?.message || 'Seen refresh failed',
                          scope: 'engaged',
                        })
                      )
                    }
                    disabled={seenLoading}
                  >
                    {seenLoading ? 'Refreshing...' : 'Refresh'}
                  </Button>
                  <Tooltip title="Toggle open notifications">
                    <IconButton
                      onClick={() => setNotificationPrefs((p) => ({ ...p, opens: !p.opens }))}
                      color={notificationPrefs.opens ? 'success' : 'default'}
                      size="small"
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              {filteredEngagedGroups.length === 0 && (
                <Alert severity="info">
                  {engagedGroups.length === 0
                    ? 'No emails have been opened yet.'
                    : 'No engaged emails match the current filters.'}
                </Alert>
              )}
              <List>
                {filteredEngagedGroups
                  .sort((a, b) => b.lastOpenedTs - a.lastOpenedTs)
                  .slice((seenPage - 1) * 10, seenPage * 10)
                  .map((grp) => (
                    <ListItem
                      key={grp.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        mb: 1,
                        alignItems: 'flex-start',
                      }}
                      secondaryAction={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            icon={<Visibility fontSize="small" sx={{ color: 'green' }} />}
                            label={`${grp.totalOpens} opens`}
                            color={grp.totalOpens >= 1 ? 'success' : 'default'}
                            size="small"
                          />
                          <Chip
                            label={`${grp.messages.length} sends`}
                            size="small"
                            variant="outlined"
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const msg = grp.messages[0];
                              setCompose((prev) => ({
                                ...prev,
                                subject: `Re: ${msg.subject || ''}`,
                                recipients: msg.recipients || [],
                                body: '',
                              }));
                              setActiveTab(1);
                            }}
                          >
                            Reach out
                          </Button>
                        </Stack>
                      }
                    >
                      <ListItemText
                        primary={grp.subjectLabel || '(No subject)'}
                        secondary={
                          <Stack spacing={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              To: {(grp.recipients || []).join(', ')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Last opened: {grp.lastOpened ? new Date(grp.lastOpened).toLocaleString() : 'N/A'}
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
              </List>
              {filteredEngagedGroups.length > 10 && (
                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSeenPage((p) => Math.max(1, p - 1))}
                    disabled={seenPage === 1}
                  >
                    Prev
                  </Button>
                  <Typography variant="body2">
                    Page {seenPage} of {engagedPageCount}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSeenPage((p) => Math.min(engagedPageCount, p + 1))}
                    disabled={seenPage >= engagedPageCount}
                  >
                    Next
                  </Button>
                </Stack>
              )}
            </Box>
          )}
          <Snackbar
            open={
              notice.open &&
              (
                notice.scope === 'global' ||
                (notice.scope === 'accounts' && activeTab === 0) ||
                (notice.scope === 'compose' && activeTab === 1) ||
                (notice.scope === 'campaign' && activeTab === 2) ||
                (notice.scope === 'inbox' && activeTab === 3) ||
                (notice.scope === 'engaged' && activeTab === 4)
              )
            }
            autoHideDuration={4000}
            onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setNotice((prev) => ({ ...prev, open: false }))}
              severity={notice.type || 'info'}
              sx={{ width: '100%' }}
            >
              {notice.message}
            </Alert>
          </Snackbar>

          <Snackbar
            open={footerNotice.open}
            onClose={() => setFooterNotice({ open: false, message: '' })}
            message={footerNotice.message}
            autoHideDuration={7000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          />
        </CardContent>
      </Card>
    </Box>

      <Dialog open={accountModalOpen} onClose={() => { setAccountModalOpen(false); setTestPassed(false); }} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 700, fontSize: '1.3rem', padding: '20px' }}>
          {editingId ? '✏️ Edit Email Account' : '📧 Add Email Account'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '85vh', overflowY: 'auto', padding: '24px' }}>
          {accountError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {accountError}
            </Alert>
          )}
          {accountMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {accountMessage}
            </Alert>
          )}

          <Stack spacing={2.5} mt={2}>
            {/* Step 1: Email Input with Auto-Detect */}
            <Card sx={{ border: '2px solid #e0e0e0', backgroundColor: '#fafafa' }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#667eea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                    1
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Enter Your Email
                  </Typography>
                </Stack>
                <TextField
                  label="Email Address"
                  placeholder="you@gmail.com"
                  value={accountForm.fromEmail}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Type your email - we'll auto-detect your provider"
                  sx={{ mb: 1 }}
                />
                {selectedProviderTemplate && (
                  <Chip
                    label={`${selectedProviderTemplate.logo} ${selectedProviderTemplate.name} Detected ✓`}
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </CardContent>
            </Card>

            {/* Step 2: Provider Selection (if not auto-detected) */}
            {!selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#667eea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                      2
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      Select Your Email Provider
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose from 13+ providers or enter manual SMTP settings:
                  </Typography>
                  
                  {/* Group providers by category */}
                  {['Popular', 'Business', 'Domain', 'Transactional', 'Custom'].map((category) => {
                    const categoryProviders = getProviderOptions().filter(p => p.category === category);
                    if (categoryProviders.length === 0) return null;
                    
                    return (
                      <Box key={category} sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea', display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {category === 'Domain' ? '🌐 Domain Email Hosting' : 
                           category === 'Business' ? '💼 Business Email' : 
                           category === 'Transactional' ? '📤 Transactional Services' :
                           category === 'Custom' ? '⚙️ Other Options' :
                           '⭐ Popular'}
                        </Typography>
                        <Grid container spacing={1}>
                          {categoryProviders.map((provider) => (
                            <Grid item xs={12} sm={6} md={4} key={provider.id}>
                              <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => handleProviderSelect(EMAIL_PROVIDERS[provider.id])}
                                sx={{
                                  py: 1.8,
                                  px: 2,
                                  border: '2px solid #e0e0e0',
                                  borderRadius: 1.5,
                                  textTransform: 'none',
                                  fontSize: '0.95rem',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  justifyContent: 'flex-start',
                                  transition: 'all 0.2s ease',
                                  backgroundColor: 'white',
                                  '&:hover': {
                                    borderColor: provider.color,
                                    backgroundColor: provider.color + '08',
                                    boxShadow: `0 4px 12px ${provider.color}20`,
                                    transform: 'translateY(-2px)',
                                  },
                                }}
                              >
                                <Box sx={{ fontSize: '1.8rem', minWidth: '32px' }}>{provider.label.split(' ')[0]}</Box>
                                <Box sx={{ textAlign: 'left', flex: 1 }}>
                                  <Box sx={{ fontWeight: 700 }}>{provider.label.split(' ').slice(1).join(' ')}</Box>
                                </Box>
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Step 3: Setup Guide & Instructions */}
            {selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #FFA500', backgroundColor: '#fff8e1' }}>
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" spacing={1} mb={1.5}>
                    <Box sx={{ fontSize: '1.5rem' }}>📺</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f57c00' }}>
                        Setup Tutorial: {selectedProviderTemplate.name}
                      </Typography>
                      <Button
                        size="small"
                        href={selectedProviderTemplate.youtubeLink || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ mt: 0.5 }}
                        endIcon={<VolumeUp />}
                      >
                        Watch YouTube Tutorial
                      </Button>
                    </Box>
                  </Stack>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ backgroundColor: 'white', p: 1.5, borderRadius: 1, maxHeight: 300, overflowY: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.9rem' }}>
                      {selectedProviderTemplate.guide}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowProviderGuide(!showProviderGuide)}
                    sx={{ mt: 1 }}
                  >
                    {showProviderGuide ? 'Hide Guide' : 'Show Full Guide'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSelectedProviderTemplate(null);
                      setShowProviderGuide(false);
                    }}
                    startIcon={<Close />}
                    sx={{ mt: 1, ml: 1, color: '#f44336', borderColor: '#f44336' }}
                  >
                    Change Provider
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Account Details */}
            {selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#667eea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                      3
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Account Details
                    </Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    <TextField
                      label="Account Label"
                      placeholder={`My ${selectedProviderTemplate.name} Account`}
                      value={accountForm.label}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, label: e.target.value }))}
                      fullWidth
                      size="small"
                      helperText="Use a descriptive name (e.g., 'Primary Gmail', 'Work Outlook')"
                    />

                    <TextField
                      label="Display Name"
                      placeholder="Your Full Name"
                      value={accountForm.fromName}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, fromName: e.target.value }))}
                      fullWidth
                      size="small"
                      helperText="Recipients will see this name when you send emails"
                    />
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Step 5: Credentials Box */}
            {selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #2196F3', backgroundColor: '#E3F2FD' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: '#2196F3', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                      4
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      🔐 Enter Your Credentials
                    </Typography>
                  </Stack>
                  
                  {selectedProviderTemplate.smtp?.requiresAppPassword && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        ⚠️ App Password Required
                      </Typography>
                      <Typography variant="body2">
                        {selectedProviderTemplate.name} requires a special "App Password", not your regular password.
                      </Typography>
                      {selectedProviderTemplate.smtp?.appPasswordUrl && (
                        <Button
                          size="small"
                          href={selectedProviderTemplate.smtp.appPasswordUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="text"
                          sx={{ mt: 0.5 }}
                        >
                          Get App Password →
                        </Button>
                      )}
                    </Alert>
                  )}

                  <Stack spacing={1.2}>
                    <TextField
                      label="Username / Email"
                      placeholder={accountForm.fromEmail || 'your.email@gmail.com'}
                      value={accountForm.username}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, username: e.target.value }))}
                      fullWidth
                      size="small"
                      helperText="Usually your email address"
                    />
                    <TextField
                      label="Password / API Key"
                      type="password"
                      placeholder="••••••••••••••••"
                      value={accountForm.password}
                      onChange={(e) => setAccountForm((prev) => ({ ...prev, password: e.target.value }))}
                      fullWidth
                      size="small"
                      helperText="Your password (encrypted, never stored as plain text)"
                    />
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Advanced SMTP Settings - Collapsible */}
            {selectedProviderTemplate && (
              <Card sx={{ border: '2px solid #9C27B0', backgroundColor: '#F3E5F5' }}>
                <CardContent>
                  <Box
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      userSelect: 'none',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ fontSize: '1.2rem' }}>⚙️</Box>
                      <Typography sx={{ fontWeight: 700 }}>Advanced SMTP Settings</Typography>
                    </Stack>
                    {showAdvanced ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                  <Collapse in={showAdvanced} sx={{ mt: 1.5 }}>
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        These settings are usually pre-filled. Only change if needed.
                      </Typography>
                      <TextField
                        label="SMTP Host"
                        placeholder="smtp.gmail.com"
                        value={accountForm.host}
                        onChange={(e) => setAccountForm((prev) => ({ ...prev, host: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField
                            label="SMTP Port"
                            type="number"
                            value={accountForm.port}
                            onChange={(e) => setAccountForm((prev) => ({ ...prev, port: Number(e.target.value) }))}
                            fullWidth
                            size="small"
                            helperText="587 (TLS) or 465 (SSL)"
                          />
                        </Grid>
                        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={accountForm.secure}
                                onChange={(e) => setAccountForm((prev) => ({ ...prev, secure: e.target.checked }))}
                                size="small"
                              />
                            }
                            label="Use TLS/SSL"
                          />
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1.5 }}>
                        IMAP (For Receiving Emails)
                      </Typography>
                      <TextField
                        label="IMAP Host"
                        placeholder="imap.gmail.com"
                        value={accountForm.imapHost}
                        onChange={(e) => setAccountForm((prev) => ({ ...prev, imapHost: e.target.value }))}
                        fullWidth
                        size="small"
                        helperText="Leave empty to disable receiving emails"
                      />
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <TextField
                            label="IMAP Port"
                            type="number"
                            value={accountForm.imapPort || 993}
                            onChange={(e) => setAccountForm((prev) => ({ ...prev, imapPort: Number(e.target.value) }))}
                            fullWidth
                            size="small"
                            helperText="Usually 993"
                          />
                        </Grid>
                        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={accountForm.imapSecure !== false}
                                onChange={(e) => setAccountForm((prev) => ({ ...prev, imapSecure: e.target.checked }))}
                                size="small"
                              />
                            }
                            label="Use Secure"
                          />
                        </Grid>
                      </Grid>
                      <TextField
                        label="Daily Send Limit"
                        type="number"
                        value={accountForm.dailyLimit || '0'}
                        onChange={(e) => setAccountForm((prev) => ({ ...prev, dailyLimit: Number(e.target.value) || 0 }))}
                        fullWidth
                        size="small"
                        helperText="0 = unlimited. Typical: 500 per day"
                      />
                    </Stack>
                  </Collapse>
                </CardContent>
              </Card>
            )}

            {/* Info: What You Can Do */}
            {selectedProviderTemplate && (
              <Alert severity="info" icon={<Check />}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  ✅ Once Added, You Can:
                </Typography>
                <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
                  <li>Send emails from this account</li>
                  <li>Receive emails in the Inbox</li>
                  <li>Track email opens and clicks</li>
                  <li>Schedule campaigns</li>
                  <li>Use multiple accounts simultaneously</li>
                </ul>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, background: '#f5f5f5', display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Button onClick={() => { setAccountModalOpen(false); setSelectedProviderTemplate(null); setShowAdvanced(false); setTestPassed(false); }}>
              Cancel
            </Button>
            {selectedProviderTemplate && (
              <Button
                variant="text"
                onClick={() => {
                  setSelectedProviderTemplate(null);
                  setAccountForm({
                    label: '',
                    fromEmail: '',
                    username: '',
                    password: '',
                    host: '',
                    port: 587,
                    secure: true,
                    imapHost: '',
                    imapPort: 993,
                    imapSecure: true,
                    mailbox: 'INBOX',
                    popHost: '',
                    popPort: 995,
                    popSecure: true,
                  });
                  setShowAdvanced(false);
                }}
                startIcon={<ExpandLess />}
                sx={{ ml: 1 }}
              >
                ← Back to Providers
              </Button>
            )}
          </Box>
          {selectedProviderTemplate && (
            <Box>
              <Button
                variant="outlined"
                onClick={() => {
                  handleAccountTest();
                }}
                disabled={!accountForm.host || !accountForm.username || !accountForm.password || testingAccount}
                startIcon={testingAccount ? <CircularProgress size={20} /> : <Mail />}
              >
                {testingAccount ? 'Testing...' : 'Test Connection'}
              </Button>
              <Button
                variant="contained"
                sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', ml: 1 }}
                onClick={async () => {
                  const saved = await handleAccountSave();
                  if (!saved) return;
                  setAccountModalOpen(false);
                  setSelectedProviderTemplate(null);
                  setShowAdvanced(false);
                  setTestPassed(false);
                }}
                disabled={!testPassed || savingAccount}
                startIcon={savingAccount ? <CircularProgress size={20} /> : <Save />}
              >
                {savingAccount ? 'Saving...' : (editingId ? 'Update Account' : 'Add Account')}
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={templateModalOpen} onClose={() => setTemplateModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{templateForm.id ? 'Edit Template' : 'New Template'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.2} mt={0.5}>
            <TextField
              label="Template name"
              size="small"
              value={templateForm.name}
              onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Subject"
              size="small"
              value={templateForm.subject}
              onChange={(e) => setTemplateForm((p) => ({ ...p, subject: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Body"
              size="small"
              multiline
              minRows={5}
              value={templateForm.body}
              onChange={(e) => setTemplateForm((p) => ({ ...p, body: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={() => {
              saveTemplate();
              setTemplateModalOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={groupModalOpen} onClose={() => setGroupModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{groupForm.id ? 'Edit Account Group' : 'Create Account Group'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Group name"
              value={groupForm.name}
              onChange={(e) => setGroupForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              select
              fullWidth
              size="small"
              label="Accounts in this group"
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  (selected || [])
                    .map((id) => accounts.find((a) => a.id === id)?.label || id)
                    .join(', '),
              }}
              value={groupForm.accountIds}
              onChange={(e) => {
                const value = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                setGroupForm((prev) => ({ ...prev, accountIds: value }));
              }}
            >
              {accounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.label || a.fromEmail || a.username}
                </MenuItem>
              ))}
              {accounts.length === 0 && <MenuItem disabled>No accounts yet</MenuItem>}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleGroupSave}>
            Save Group
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailWorkspace;



